import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '@/components/Layout';
import { API, useAuth } from '@/App';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Bed, Compass, Bus, UserCheck, Save, Loader2, Pencil, X,
  CalendarDays, IndianRupee, CreditCard, Clock, MapPin, Phone, Trash2, Plus,
  Upload, FileText, Download, Paperclip, Heart, Leaf, Package, Users, Sparkles,
  Link as LinkIcon,
  Image as ImageIcon,
  ArrowDownUp, ChevronDown, XCircle,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format, parseISO, eachDayOfInterval } from 'date-fns';
import { formatDate, nightsBetween } from '@/lib/dateUtils';
import ServicePayments from '@/components/ServicePayments';
import RazorpayPanel from '@/components/RazorpayPanel';
import DocumentsPanel from '@/components/DocumentsPanel';
import BrochureManager from '@/components/BrochureManager';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import LocationAutocomplete from '@/components/LocationAutocomplete';
import LocationPinBtn from '@/components/LocationPinBtn';
import HotelMapUrlField from '@/components/HotelMapUrlField';
import CustomerLinkPicker from '@/components/CustomerLinkPicker';
import { CareMomentsCard } from '@/components/CareMoments';
import ContactSearchInput from '@/components/ContactSearchInput';
import DocumentGallery from '@/components/DocumentGallery';
import DayStopsEditor from '@/components/DayStopsEditor';
import { inheritCityPin } from '@/lib/inheritCityPin';
import { SortableCollapsibleCard } from '@/components/SortableCollapsibleCard';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import DayActivitiesEditor from '@/components/DayActivitiesEditor';
import PinButton from '@/components/PinButton';
import { usePollenCapabilities } from '@/hooks/usePollenCapabilities';
import { useShowProfits } from '@/hooks/useShowProfits';

// Tour lifecycle statuses. Kept deliberately simple — Booked covers every
// "confirmed/done" outcome. Cancelled is excluded from KPIs (it's lost
// business, not closed business). The dashboard treats everything except
// Planning + Cancelled as closed.
const statusColors = {
  Planning: 'bg-gray-100 text-gray-800',
  Booked: 'bg-emerald-100 text-emerald-800',
  Ongoing: 'bg-amber-100 text-amber-800',
  Completed: 'bg-emerald-100 text-emerald-800',
  Cancelled: 'bg-rose-100 text-rose-700',
};
const TOUR_STATUSES = ['Planning', 'Booked', 'Ongoing', 'Completed', 'Cancelled'];

const PollenDotsTourDetail = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const caps = usePollenCapabilities();
  const [showProfits] = useShowProfits();
  // Profit / cost visibility — STRICTLY Super Admin only. Admin and any
  // other role must never see Total Cost or Gross Profit because that
  // would let them reverse-engineer the company margin. Vendor payments
  // and customer price remain editable for Admin (separately gated).
  const profitVisible = caps.is_super_admin && showProfits;
  const { id } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  // Category for the next file being uploaded (Invoice | Itinerary | Document)
  const [uploadCategory, setUploadCategory] = useState('Document');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  // Customer-name dropdown state (mirrors Add Tour page UX)
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const customerFieldRef = React.useRef(null);
  const [groupExtractOpen, setGroupExtractOpen] = useState(false);
  // Accordion state for service lists — only ONE card per list (hotels /
  // activities / transport / guides) can be expanded at a time so the form
  // is compact and the operator isn't endlessly scrolling. Keyed by
  // `${listName}-${stableId}`. Stable IDs are kept in `_cid` on each row
  // so reorders don't collapse what the user just expanded.
  const [expandedCard, setExpandedCard] = useState({});
  const ensureCid = React.useCallback((items) => {
    if (!Array.isArray(items)) return items;
    let mutated = false;
    const out = items.map(it => {
      if (it && typeof it === 'object' && !it._cid) {
        mutated = true;
        return { ...it, _cid: `c_${Math.random().toString(36).slice(2, 10)}` };
      }
      return it;
    });
    return mutated ? out : items;
  }, []);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );
  const handleReorder = (listName) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setEditData(prev => {
      if (!prev) return prev;
      const list = ensureCid(prev[listName] || []);
      const oldIdx = list.findIndex(x => x._cid === active.id);
      const newIdx = list.findIndex(x => x._cid === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return { ...prev, [listName]: arrayMove(list, oldIdx, newIdx) };
    });
  };
  const toggleExpand = (listName, cid) => {
    setExpandedCard(prev => {
      const key = `${listName}-${cid}`;
      // Toggling the open card → collapse it. Tapping any other card →
      // close the previous and open the new one (only one at a time).
      const cur = prev[listName];
      return { ...prev, [listName]: cur === cid ? null : cid };
    });
  };
  // "Sort by date" — one-tap chronological sort for each service list.
  // Uses the column most operators expect: hotels by check_in, activities
  // by date, transport by start_date (falling back to legacy `date`),
  // guides by start_date. Rows without a date sink to the bottom so they
  // don't silently jump around.
  const SORT_KEY = { hotels: 'check_in', activities: 'date', transport: 'start_date', guides: 'start_date' };
  const sortByDate = (listName) => () => {
    setEditData(prev => {
      if (!prev) return prev;
      const k = SORT_KEY[listName];
      const list = ensureCid(prev[listName] || []);
      const sorted = [...list].sort((a, b) => {
        const da = (a[k] || (listName === 'transport' && a.date) || '').toString();
        const db = (b[k] || (listName === 'transport' && b.date) || '').toString();
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da.localeCompare(db);
      });
      return { ...prev, [listName]: sorted };
    });
    toast.success('Sorted by date');
  };
  const [groupExtractText, setGroupExtractText] = useState('');
  const [groupExtracting, setGroupExtracting] = useState(false);
  const [relinking, setRelinking] = useState(false);
  // Per-row expand toggle for the Family & Co-travellers card. Defaults to
  // all collapsed — operators with 6+ co-travellers on a tour see only a
  // tidy one-liner summary per row and tap to edit, rather than a wall of
  // fields. Newly added rows auto-expand (handled inside addGroupMember).
  const [groupExpanded, setGroupExpanded] = useState(() => new Set());

  const authHeaders = () => {
    const h = {};
    const t = localStorage.getItem('session_token');
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
  };
  const authFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...authHeaders() } });

  const fetchTour = useCallback(async () => {
    try {
      const [tourRes, payRes, logRes, filesRes, vendorRes, custRes] = await Promise.all([
        authFetch(`${POLLEN_API}/tours/${id}`),
        authFetch(`${POLLEN_API}/payments?tour_id=${id}`),
        authFetch(`${POLLEN_API}/activity-logs?limit=20`),
        authFetch(`${POLLEN_API}/files?tour_id=${id}`),
        authFetch(`${POLLEN_API}/vendors`),
        authFetch(`${POLLEN_API}/customers`),
      ]);
      if (tourRes.ok) { const d = await tourRes.json(); d.initiatives = d.initiatives || []; d.care = d.care || []; d.other_expenses = d.other_expenses || []; d.group_members = d.group_members || []; setTour(d); setEditData(d); }
      else { toast.error('Tour not found'); navigate('/pollen-dots/tours'); return; }
      if (payRes.ok) setPayments(await payRes.json());
      if (logRes.ok) {
        const logs = await logRes.json();
        setActivityLogs(logs.filter(l => l.entity_id === id));
      }
      if (filesRes.ok) setFiles(await filesRes.json());
      if (vendorRes.ok) setVendors(await vendorRes.json());
      if (custRes.ok) setCustomers(await custRes.json());
    } catch (e) { toast.error('Failed to load tour'); }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchTour(); }, [fetchTour]);

  const cancelTour = async () => {
    if (!window.confirm('Cancel this tour? Linked quotes and their unpaid payment links will also be cancelled.')) return;
    const r = await authFetch(`${API}/docs/tours/${id}/cancel`, { method: 'POST' });
    const d = await r.json().catch(() => ({}));
    if (r.ok) {
      toast.success(`Tour cancelled${d.cancelled_quotes?.length ? ` · quote ${d.cancelled_quotes.join(', ')} cancelled too` : ''}`);
      if (d.amount_received > 0) toast.info(`₹${d.amount_received.toLocaleString('en-IN')} was received on this booking — issue a refund from the Documents tab if applicable.`, { duration: 9000 });
      fetchTour();
    } else toast.error(d.detail || 'Failed to cancel tour');
  };

  // Keep the itinerary in lock-step with start_date / end_date while editing.
  // When the operator extends or shortens a tour, regenerate the day array
  // so the Itinerary tab shows every day from start to end (inclusive).
  // Existing per-day data (overnight_location, stops, etc.) is preserved
  // by matching on date — only new days are added or stale ones trimmed.
  useEffect(() => {
    if (!editing || !editData?.start_date || !editData?.end_date) return;
    try {
      const start = parseISO(editData.start_date);
      const end = parseISO(editData.end_date);
      if (end < start) return;
      const days = eachDayOfInterval({ start, end });
      const existing = editData.itinerary || [];
      const next = days.map((d, i) => {
        const dateStr = format(d, 'yyyy-MM-dd');
        const keep = existing.find(e => e.date === dateStr);
        return keep
          ? { ...keep, day_number: i + 1, date: dateStr, weekday: format(d, 'EEEE') }
          : { day_number: i + 1, date: dateStr, weekday: format(d, 'EEEE'), overnight_location: '' };
      });
      // Only update if the day count or dates actually changed — avoids an
      // infinite re-render loop because the dependency array references the
      // same object reference.
      const same = next.length === existing.length
        && next.every((d, i) => existing[i] && existing[i].date === d.date);
      if (!same) {
        setEditData(prev => ({ ...prev, itinerary: next }));
      }
    } catch (_) { /* invalid date — leave alone */ }
  }, [editing, editData?.start_date, editData?.end_date, editData?.itinerary]);

  // When entering edit mode (or when fresh tour data loads while editing),
  // ensure every hotel/activity/transport/guide row carries a stable `_cid`
  // for the sortable+collapsible cards. Backfilling here means existing
  // tours saved before this feature work seamlessly.
  useEffect(() => {
    if (!editing || !editData) return;
    const needs = (arr) => Array.isArray(arr) && arr.some(x => x && typeof x === 'object' && !x._cid);
    if (!needs(editData.hotels) && !needs(editData.activities)
        && !needs(editData.transport) && !needs(editData.guides)) return;
    setEditData(prev => ({
      ...prev,
      hotels: ensureCid(prev.hotels),
      activities: ensureCid(prev.activities),
      transport: ensureCid(prev.transport),
      guides: ensureCid(prev.guides),
    }));
  }, [editing, editData, ensureCid]);

  // Close the customer dropdown when clicking anywhere outside the field.
  useEffect(() => {
    if (!customerDropdownOpen) return;
    const handler = (e) => {
      if (customerFieldRef.current && !customerFieldRef.current.contains(e.target)) {
        setCustomerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [customerDropdownOpen]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tour_id', id);
    formData.append('category', uploadCategory || 'Document');
    try {
      const res = await authFetch(`${POLLEN_API}/files/upload`, { method: 'POST', body: formData });
      if (res.ok) { toast.success(`${uploadCategory} uploaded`); fetchTour(); }
      else toast.error('Upload failed');
    } catch (err) { toast.error('Upload error'); }
    setUploading(false);
    e.target.value = '';
  };

  const setFileCategory = async (fileId, category) => {
    const res = await authFetch(`${POLLEN_API}/files/${fileId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category }),
    });
    if (res.ok) { toast.success(`Re-tagged as ${category}`); fetchTour(); }
    else toast.error('Failed to re-categorize');
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await authFetch(`${POLLEN_API}/files/${fileId}/download`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error('Download failed'); }
  };

  const deleteFile = async (fileId) => {
    await authFetch(`${POLLEN_API}/files/${fileId}`, { method: 'DELETE' });
    toast.success('File removed');
    setDeleteConfirm(null);
    fetchTour();
  };

  const handleSave = async () => {
    if (!editData.invoice_date) {
      toast.error('Please enter an invoice date \u2014 it decides which fiscal year this tour belongs to');
      return;
    }
    setSaving(true);
    try {
      // Strip the transient `_cid` field we attach to each list row for
      // collapsible+sortable state — backend has no use for it and we
      // don't want the schema to absorb leftover client-only keys.
      const stripCid = (arr) => Array.isArray(arr)
        ? arr.map(({ _cid, ...rest }) => rest)
        : arr;
      const payload = {
        ...editData,
        hotels: stripCid(editData.hotels),
        activities: stripCid(editData.activities),
        transport: stripCid(editData.transport),
        guides: stripCid(editData.guides),
      };
      const res = await authFetch(`${POLLEN_API}/tours/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) { toast.success('Tour updated'); setEditing(false); fetchTour(); }
      else { const err = await res.json(); toast.error(err.detail || 'Update failed'); }
    } catch (e) { toast.error('Error saving'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      const res = await authFetch(`${POLLEN_API}/tours/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Tour deleted'); navigate('/pollen-dots/tours'); }
      else toast.error('Delete failed');
    } catch { toast.error('Error deleting'); }
    setDeleteConfirm(null);
  };

  const updateEdit = (field, value) => setEditData(prev => ({ ...prev, [field]: value }));  const updateEditList = (list, idx, field, value) => {
    setEditData(prev => {
      const items = [...(prev[list] || [])];
      items[idx] = { ...items[idx], [field]: value };
      if (list === 'hotels' && (field === 'check_in' || field === 'check_out')) {
        const ci = items[idx].check_in;
        const co = items[idx].check_out;
        items[idx].nights = (ci && co) ? Math.max(0, Math.round((Date.parse(`${String(co).slice(0,10)}T00:00:00Z`) - Date.parse(`${String(ci).slice(0,10)}T00:00:00Z`)) / 86400000)) : 0;
      }
      if (list === 'hotels' && (field === 'total_amount' || field === 'advance_paid')) {
        items[idx].balance = (parseFloat(items[idx].total_amount) || 0) - (parseFloat(items[idx].advance_paid) || 0);
      }
      if ((list === 'activities' || list === 'transport') && (field === 'total_amount' || field === 'advance')) {
        items[idx].balance = (parseFloat(items[idx].total_amount) || 0) - (parseFloat(items[idx].advance) || 0);
      }
      return { ...prev, [list]: items };
    });
  };

  const addEditItem = (list, template) => {
    setEditData(prev => ({
      ...prev,
      [list]: [
        ...(prev[list] || []),
        // Stable per-row id used by the sortable/collapsible cards. Generated
        // here (not on render) so reorder + expand state survive across
        // edits. Stripped before saving the tour — see `update_tour`
        // request below.
        { ...template, _cid: `c_${Math.random().toString(36).slice(2, 10)}` },
      ],
    }));
  };
  const removeEditItem = (list, idx) => {
    setEditData(prev => ({ ...prev, [list]: (prev[list] || []).filter((_, i) => i !== idx) }));
  };

  // --- Phase 4: Group Members (saves tour with just group_members updated) ---
  const saveGroupMembers = async (newList) => {
    try {
      const payload = { ...tour, group_members: newList };
      const res = await authFetch(`${POLLEN_API}/tours/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (res.ok) {
        setTour(t => ({ ...t, group_members: newList }));
        if (editing) setEditData(d => ({ ...d, group_members: newList }));
        toast.success('Group saved');
      } else toast.error('Failed to save');
    } catch { toast.error('Error'); }
  };
  const addGroupMember = () => {
    const next = [...(tour.group_members || []), { name: '', dob: '', anniversary: '', phone: '', notes: '' }];
    // Auto-expand the newly added row so the operator can start typing
    // immediately without an extra tap.
    setGroupExpanded(prev => {
      const nxt = new Set(prev);
      nxt.add(next.length - 1);
      return nxt;
    });
    saveGroupMembers(next);
  };

  const toggleGroupExpanded = (i) => {
    setGroupExpanded(prev => {
      const nxt = new Set(prev);
      if (nxt.has(i)) nxt.delete(i); else nxt.add(i);
      return nxt;
    });
  };
  const updateGroupLocal = (i, field, val) => {
    const next = (tour.group_members || []).map((m, idx) => idx === i ? { ...m, [field]: val } : m);
    setTour(t => ({ ...t, group_members: next }));
    if (editing) setEditData(d => ({ ...d, group_members: next }));
  };
  const removeGroupMember = (i) => saveGroupMembers((tour.group_members || []).filter((_, idx) => idx !== i));

  // Link / unlink a group_member to an existing customer record. Once linked,
  // /api/public/journey for THAT customer will auto-include this tour's dots.
  const linkGroupMember = (i, c) => {
    const next = (tour.group_members || []).map((m, idx) => idx === i
      ? {
          ...m,
          customer_id: c.id,
          name: c.name || m.name,
          dob: c.dob || m.dob,
          // Inherit any contact info already on the customer record so the
          // tour-side row matches the source of truth. Operator can still
          // override either field by typing over it.
          anniversary: c.anniversary || m.anniversary || '',
          phone: c.phone || m.phone || '',
        }
      : m);
    saveGroupMembers(next);
  };

  // Triggered when the operator picks a row from the ContactSearchInput
  // dropdown. Auto-prefills DOB / Anniversary / Phone / Relation from the
  // matched record AND, when the match is a Pollen Dots customer, links
  // the group member to that customer_id so this tour starts appearing
  // on their journey map automatically. Kills the long-standing "we keep
  // re-typing the same co-traveller on every tour" duplication.
  const pickContactForGroupMember = (i, rec) => {
    const next = (tour.group_members || []).map((m, idx) => {
      if (idx !== i) return m;
      const isCustomer = rec.via === 'Customer' || !rec.via;
      return {
        ...m,
        name: rec.name || m.name,
        dob: rec.dob || m.dob || '',
        anniversary: rec.anniversary || m.anniversary || '',
        phone: rec.phone || m.phone || '',
        relation: rec.relation || m.relation || '',
        // Only carry the customer_id when the match is an actual customer
        // record; family / co-traveller-only matches don't have a profile
        // page to link to.
        customer_id: isCustomer && rec.id ? rec.id : m.customer_id,
      };
    });
    saveGroupMembers(next);
  };
  const unlinkGroupMember = (i) => {
    const next = (tour.group_members || []).map((m, idx) => idx === i
      ? { ...m, customer_id: null }
      : m);
    saveGroupMembers(next);
  };

  // One-tap clean-up: fuzzy-match every unlinked co-traveller against the
  // customer DB by (name + phone10) / (name + dob) / unique-name and link
  // the matches in bulk. Catches the historical duplication created before
  // ContactSearchInput was wired into the Name field.
  const handleRelinkGroup = async () => {
    if (!window.confirm(
      'Scan this tour\'s Family & Co-travellers and auto-link each to ' +
      'their existing Pollen Dots customer record (when a clear match exists)?'
    )) return;
    setRelinking(true);
    try {
      const res = await authFetch(`${POLLEN_API}/tours/${id}/relink-group`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Relink failed');
      const data = await res.json();
      if (data.linked === 0 && data.already_linked > 0) {
        toast.success(`All ${data.already_linked} co-travellers were already linked.`);
      } else if (data.linked === 0) {
        toast.message('No safe matches found',
          { description: data.unmatched.length
              ? `${data.unmatched.length} co-travellers couldn't be auto-matched — link them manually below.`
              : 'Nothing to link.' });
      } else {
        toast.success(`Linked ${data.linked} co-traveller${data.linked === 1 ? '' : 's'} to existing customers.`,
          { description: data.unmatched.length
              ? `${data.unmatched.length} still need manual linking.`
              : 'Their tours will now appear on each customer\'s journey map.' });
      }
      // Pull the fresh tour so the UI shows the new customer_id badges.
      if (data.linked > 0) {
        setTour(t => ({ ...t, group_members: data.members_after }));
        if (editing) setEditData(d => ({ ...d, group_members: data.members_after }));
      }
    } catch (e) {
      toast.error(e.message || 'Could not auto-link co-travellers');
    } finally {
      setRelinking(false);
    }
  };
  const mergeExtractedGroup = (data) => {
    const newMembers = (data.group_members || []).filter(m => m.name);
    if (newMembers.length === 0) { toast.error('No group members detected'); return; }
    saveGroupMembers([...(tour.group_members || []), ...newMembers]);
    setGroupExtractOpen(false);
    setGroupExtractText('');
  };
  const handleGroupExtractText = async () => {
    if (!groupExtractText.trim()) return;
    setGroupExtracting(true);
    try {
      const res = await authFetch(`${POLLEN_API}/tours/${id}/extract-group-text`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: groupExtractText }),
      });
      if (res.ok) mergeExtractedGroup(await res.json()); else toast.error('Extraction failed');
    } catch { toast.error('Error'); }
    setGroupExtracting(false);
  };
  const handleGroupExtractFile = async (file) => {
    if (!file) return;
    setGroupExtracting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await authFetch(`${POLLEN_API}/tours/${id}/extract-group`, { method: 'POST', body: fd });
      if (res.ok) mergeExtractedGroup(await res.json()); else { const e = await res.json(); toast.error(e.detail || 'Extraction failed'); }
    } catch { toast.error('Error'); }
    setGroupExtracting(false);
  };

  // True "paid so far" for a service line = sum of every payment row in
  // pollen_payments matching this tour + category + vendor (by id first,
  // by name as fallback). This is the SOURCE OF TRUTH — the `advance_paid`
  // / `advance` field on the hotel/activity/transport record is a stale
  // snapshot that only reflects the first advance entered at booking time,
  // not subsequent additions made via the ServicePayments widget.
  //
  // Fallback: if the line has NO matching payment rows at all but its
  // legacy `advance` / `advance_paid` field is > 0, we use that so historic
  // bookings that were never re-recorded through ServicePayments still
  // display a sensible Paid value.
  // NB: Both hooks must run on EVERY render (above the early returns) to
  // satisfy React's rules of hooks.
  const paymentsByService = useMemo(() => {
    const byKey = new Map(); // key = `${category}|${vendorId or name}`
    for (const p of (payments || [])) {
      const cat = (p.category || '').toLowerCase();
      const k1 = `${cat}|id:${p.vendor_id || ''}`;
      const k2 = `${cat}|name:${(p.vendor_name || '').trim().toLowerCase()}`;
      const amt = Number(p.amount || 0);
      byKey.set(k1, (byKey.get(k1) || 0) + amt);
      byKey.set(k2, (byKey.get(k2) || 0) + amt);
    }
    return byKey;
  }, [payments]);
  const sumPaidFor = useCallback((category, vendorId, vendorName, legacyAdvance = 0) => {
    const cat = (category || '').toLowerCase();
    let sum = 0;
    if (vendorId) {
      const v = paymentsByService.get(`${cat}|id:${vendorId}`);
      if (typeof v === 'number') sum = v;
    }
    if (!sum) {
      sum = paymentsByService.get(`${cat}|name:${(vendorName || '').trim().toLowerCase()}`) || 0;
    }
    if (!sum && legacyAdvance > 0) return Number(legacyAdvance) || 0;
    return sum;
  }, [paymentsByService]);

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  if (!tour) return <Layout><div className="text-center py-20 text-muted-foreground">Tour not found</div></Layout>;

  const data = editing ? editData : tour;
  // Mixed tours combine direct customer payments WITH agent referral commissions,
  // so the agent details block must surface for both 'Agent' and 'Mixed' sources.
  const isAgent = data.source_type === 'Agent' || data.source_type === 'Mixed';
  const totalCost = (isAgent ? (parseFloat(data.agent_details?.total_package_cost) || 0) : 0)
    + (data.hotels || []).reduce((s, h) => s + (parseFloat(h.total_amount) || 0), 0)
    + (data.activities || []).reduce((s, a) => s + (parseFloat(a.total_amount) || 0), 0)
    + (data.transport || []).reduce((s, t) => s + (parseFloat(t.total_amount) || 0), 0)
    + (data.guides || []).reduce((s, g) => s + (parseFloat(g.total_amount) || 0), 0)
    + (data.initiatives || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
    + (data.care || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0)
    + (data.other_expenses || []).reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
  const grossProfit = (parseFloat(data.customer_amount) || 0) - totalCost;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 min-w-0" data-testid="tour-detail-page">
        {/* Header — mobile-first: title row + horizontally scrollable action strip */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3 min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="sm" className="shrink-0 -ml-2" onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/pollen-dots/tours'); }} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-2xl font-bold tracking-tight truncate" data-testid="tour-title">{data.tour_name}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge className={statusColors[data.status] || 'bg-gray-100'}>{data.status}</Badge>
                <span className="text-xs text-muted-foreground">{data.source_type}</span>
                <span className="text-xs text-muted-foreground">{formatDate(data.start_date)} to {formatDate(data.end_date)}</span>
              </div>
            </div>
          </div>
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto sm:overflow-visible scrollbar-none">
            <div className="flex items-center gap-2 sm:flex-wrap sm:justify-end whitespace-nowrap">
              {editing ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditData(tour); }}><X className="w-4 h-4 mr-1" />Cancel</Button>
                  <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save
                  </Button>
                </>
              ) : (
                <>
                  <PinButton entityType="tour" entityId={data.id} />
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-btn"><Pencil className="w-4 h-4 mr-1" />Edit</Button>
                  <Button variant="outline" size="sm"
                          className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                          onClick={() => setBrochureOpen(true)}
                          data-testid="tour-brochure-btn">
                    <Sparkles className="w-4 h-4 mr-1" />Brochures
                  </Button>
                  {data.status !== 'Cancelled' && (
                    <Button variant="outline" size="sm" className="border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100" onClick={cancelTour} data-testid="cancel-tour-btn"><XCircle className="w-4 h-4 mr-1" />Cancel Tour</Button>
                  )}
                  <Button variant="outline" size="sm" className="text-destructive" onClick={() => setDeleteConfirm('tour')} data-testid="delete-btn"><Trash2 className="w-4 h-4" /></Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Save/Cancel bar at bottom when editing */}
        {editing && (
          <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 z-[60] bg-background border-t shadow-lg p-3 flex justify-end gap-2 lg:left-64">
            <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditData(tour); }}><X className="w-4 h-4 mr-1" />Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-btn-bottom">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save Changes
            </Button>
          </div>
        )}

        {/* Financial Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-blue-200 bg-blue-50/50"><CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground">Customer Amount</p>
            {editing ? <Input type="number" className="h-8 mt-1 text-sm" value={editData.customer_amount} onChange={e => updateEdit('customer_amount', e.target.value)} />
              : <p className="text-xl font-bold">{parseFloat(data.customer_amount || 0).toLocaleString('en-IN')}</p>}
          </CardContent></Card>
          <Card style={{ display: profitVisible ? undefined : 'none' }}><CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground">Total Cost</p>
            {data.is_historical
              ? <p className="text-xl font-bold text-muted-foreground">—</p>
              : <p className="text-xl font-bold">{totalCost.toLocaleString('en-IN')}</p>}
          </CardContent></Card>
          <Card className={data.is_historical ? 'border-slate-200 bg-slate-50/50' : (grossProfit >= 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50')} style={{ display: profitVisible ? undefined : 'none' }}><CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground">Gross Profit</p>
            {data.is_historical
              ? <div><p className="text-xl font-bold text-muted-foreground">—</p><p className="text-[10px] text-muted-foreground">Historical (pre FY 2026-27)</p></div>
              : <p className={`text-xl font-bold ${grossProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{grossProfit.toLocaleString('en-IN')}</p>}
          </CardContent></Card>
        </div>

        {data.is_historical && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <strong>Historical Tour</strong> — this tour started before 1 Apr 2026 (FY 2026-27 cutoff). Revenue contributes to customer lifecycle stats; profit tracking starts from FY 2026-27 (P&L records cover earlier periods).
          </div>
        )}

        <Tabs defaultValue="details" className="w-full">
          <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto scrollbar-none">
            <TabsList className="inline-flex whitespace-nowrap">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="itinerary">Itinerary</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              <TabsTrigger value="activity">Activity Log</TabsTrigger>
            </TabsList>
          </div>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><UserCheck className="w-4 h-4" />Customer & Trip</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {/* Customer — editable picker with dropdown (same UX as Add Tour page) */}
                  <div className="col-span-2 relative" ref={customerFieldRef}>
                    <Label className="text-xs text-muted-foreground">Customer</Label>
                    {editing ? (
                      <>
                        <Input className="h-8 text-sm mt-0.5"
                          value={editData.customer_name || ''}
                          onChange={e => { updateEdit('customer_name', e.target.value); updateEdit('customer_id', ''); setCustomerDropdownOpen(true); }}
                          onFocus={() => setCustomerDropdownOpen(true)}
                          placeholder="Search or type customer name"
                          data-testid="tour-edit-customer-input"
                          autoComplete="off" />
                        {customerDropdownOpen && !editData.customer_id && (() => {
                          const q = (editData.customer_name || '').trim().toLowerCase();
                          const matches = q
                            ? customers.filter(c => (c.name || '').toLowerCase().includes(q)).slice(0, 8)
                            : [];
                          const showAddNew = q.length > 0;
                          if (matches.length === 0 && !showAddNew) return null;
                          return (
                            <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="tour-edit-customer-dropdown">
                              {matches.length > 0 && (
                                <>
                                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/50 border-b">Existing customers</div>
                                  {matches.map(c => (
                                    <button key={c.id} type="button"
                                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between items-center gap-2"
                                      onClick={() => {
                                        updateEdit('customer_name', c.name);
                                        updateEdit('customer_id', c.id);
                                        setCustomerDropdownOpen(false);
                                      }}
                                      data-testid={`tour-edit-customer-option-${c.id}`}>
                                      <div className="min-w-0 flex-1">
                                        <div className="font-medium truncate">{c.name}</div>
                                        {(c.phone || c.email) && <div className="text-xs text-muted-foreground truncate">{c.phone}{c.phone && c.email ? ' · ' : ''}{c.email}</div>}
                                      </div>
                                    </button>
                                  ))}
                                </>
                              )}
                              {showAddNew && (
                                <button type="button"
                                  className="w-full text-left px-3 py-2 border-t text-sm flex items-center gap-2 hover:bg-emerald-50 text-emerald-700 font-medium"
                                  onClick={() => { updateEdit('customer_id', ''); setCustomerDropdownOpen(false); toast.success('New customer will be created on save'); }}
                                  data-testid="tour-edit-customer-add-new">
                                  <Plus className="w-4 h-4" />
                                  Add new customer: <span className="font-semibold">"{editData.customer_name}"</span>
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        {editData.customer_id && (
                          <p className="text-xs text-emerald-600 mt-1">Existing customer linked
                            <button type="button" className="underline text-muted-foreground ml-2"
                              onClick={() => { updateEdit('customer_id', ''); setCustomerDropdownOpen(true); }}
                              data-testid="tour-edit-change-customer">change</button>
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm font-medium">{data.customer_name || '-'}</p>
                    )}
                  </div>
                  {/* Source — editable Select */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Source</Label>
                    {editing ? (
                      <Select value={editData.source_type || 'Direct'} onValueChange={v => updateEdit('source_type', v)}>
                        <SelectTrigger className="h-8 text-sm mt-0.5" data-testid="tour-edit-source-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['Direct', 'OTA', 'Agent', 'Mixed'].map(s => (
                            <SelectItem key={s} value={s} data-testid={`tour-edit-source-${s.toLowerCase()}`}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : <p className="text-sm font-medium">{data.source_type || '-'}</p>}
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">Destination</Label>
                      {editing && editData.destination && <LocationPinBtn locationName={editData.destination} />}
                    </div>
                    {editing ? <LocationAutocomplete value={editData.destination} onChange={v => updateEdit('destination', v)} type="city" className="h-7 text-sm mt-0.5" />
                      : <p className="text-sm font-medium">{data.destination || '-'}</p>}
                    {editing && (
                      <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                        <input type="checkbox" className="h-4 w-4 rounded accent-primary"
                          checked={!!editData.is_international}
                          onChange={e => updateEdit('is_international', e.target.checked)}
                          data-testid="toggle-international-edit" />
                        <span className="text-[11px] text-muted-foreground">🌍 International tour</span>
                      </label>
                    )}
                    {!editing && data.is_international && (
                      <p className="text-[10px] text-amber-700 mt-0.5">🌍 International</p>
                    )}
                  </div>
                  <DetailField label="Start Date" value={data.start_date} editing={editing} type="date" onChange={v => updateEdit('start_date', v)} />
                  <DetailField label="End Date" value={data.end_date} editing={editing} type="date" onChange={v => updateEdit('end_date', v)} />
                  <DetailField label="Invoice Date" value={data.invoice_date} editing={editing} type="date" onChange={v => updateEdit('invoice_date', v)} />
                  <DetailField label="Adults" value={data.adults} editing={editing} type="number" onChange={v => updateEdit('adults', parseInt(v) || 0)} />
                  <DetailField label="Children" value={data.children} editing={editing} type="number" onChange={v => updateEdit('children', parseInt(v) || 0)} />
                  {editing && <div className="col-span-2"><Label className="text-xs">Status</Label>
                    <Select value={editData.status} onValueChange={v => updateEdit('status', v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{TOUR_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select></div>}
                  {editing && (
                    <div className="col-span-2 flex items-start gap-2 p-2 rounded-lg border border-amber-200 bg-amber-50/50">
                      <input
                        type="checkbox"
                        id="is-historical-toggle"
                        className="mt-0.5 w-4 h-4 accent-amber-600 cursor-pointer"
                        checked={!!editData.is_historical}
                        onChange={e => updateEdit('is_historical', e.target.checked)}
                        data-testid="is-historical-checkbox"
                      />
                      <label htmlFor="is-historical-toggle" className="text-xs leading-tight cursor-pointer">
                        <span className="font-semibold text-amber-900">Mark as Historical</span>
                        <span className="block text-[11px] text-amber-800/80 mt-0.5">
                          Excludes this tour from <em>Current Year</em> and <em>Last Year</em> dashboard KPIs. Still counted in Lifetime. Use this for backfilled trips from prior FYs.
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            {/* Group / Travelling With (Phase 4) */}
            <Card data-testid="group-section">
              <CardHeader className="pb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" />Family & Co-travellers ({(tour.group_members || []).length})</CardTitle>
                {/* Buttons wrap on mobile and collapse to icon-only when the
                    row would otherwise overflow the card width. The `sm:`
                    breakpoint restores the labels on tablet+. */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  <Button variant="outline" size="sm" onClick={handleRelinkGroup} disabled={relinking || (tour.group_members || []).length === 0} data-testid="group-relink-btn" title="Find and auto-link existing customer contacts for every co-traveller in this tour" className="h-8 px-2">
                    {relinking ? <Loader2 className="w-4 h-4 sm:mr-1 animate-spin" /> : <LinkIcon className="w-4 h-4 sm:mr-1" />}
                    <span className="hidden sm:inline">Link existing</span>
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setGroupExtractOpen(true)} data-testid="group-extract-btn" className="h-8 px-2" title="AI Extract from passport scans or pax list"><Sparkles className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">AI Extract</span></Button>
                  <Button variant="outline" size="sm" onClick={addGroupMember} data-testid="add-group-btn" className="h-8 px-2" title="Add co-traveller"><Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Add</span></Button>
                </div>
              </CardHeader>
              <CardContent>
                {(tour.group_members || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2 text-center">No group members added. AI Extract from passport scans or pax list.</p>
                ) : (
                  <div className="space-y-2">
                    {tour.group_members.map((m, i) => {
                      const isOpen = groupExpanded.has(i);
                      // Tidy one-line summary the operator sees when the
                      // row is collapsed. Joined-from/until and any partial
                      // trip note bubble up so they can spot anomalies
                      // without expanding every row.
                      const summaryBits = [
                        m.relation,
                        m.dob && `🎂 ${m.dob.slice(0, 10)}`,
                        m.phone,
                        (m.joined_from || m.joined_until) && 'Partial trip',
                      ].filter(Boolean);
                      return (
                      <div key={i} className="border rounded relative" data-testid={`group-row-${i}`}>
                        {/* Always-visible summary strip; tap anywhere on it
                            (except the trash icon) to toggle the editor. */}
                        <button
                          type="button"
                          onClick={() => toggleGroupExpanded(i)}
                          className="w-full flex items-center justify-between gap-2 px-2 py-2 text-left hover:bg-muted/30 rounded"
                          data-testid={`group-row-toggle-${i}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm font-medium truncate">
                              <span className="truncate">{m.name || <span className="text-muted-foreground italic">Unnamed co-traveller</span>}</span>
                              {m.customer_id && (
                                <LinkIcon className="w-3 h-3 text-emerald-600 flex-none" title="Linked to a Pollen Dots customer" />
                              )}
                            </div>
                            {summaryBits.length > 0 && (
                              <div className="text-[11px] text-muted-foreground truncate">
                                {summaryBits.join(' · ')}
                              </div>
                            )}
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 flex-none text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {isOpen && (
                        <div className="p-2 pt-0 space-y-1.5 border-t">
                        <Button variant="ghost" size="sm" className="absolute top-1 right-9 h-6 w-6 p-0 text-destructive z-10" onClick={(e) => { e.stopPropagation(); removeGroupMember(i); }}><Trash2 className="w-3 h-3" /></Button>
                        <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 pt-2">
                          <div className="sm:col-span-2 pr-6"><Label className="text-xs">Name</Label>
                            {m.customer_id ? (
                              /* Already linked → lock the name to mirror the
                                 customer record. Operator can Unlink (button
                                 below) to edit freely again. */
                              <Input className="h-8 text-sm" value={m.name || ''} disabled
                                     data-testid={`group-name-${i}`} />
                            ) : (
                              /* Typeahead across customers + family + every
                                 prior co-traveller. Picking a row prefills
                                 DOB / phone / anniversary / relation AND
                                 links to the matched customer (when match
                                 is a customer) so we stop re-typing the
                                 same person on every tour. */
                              <ContactSearchInput
                                value={m.name || ''}
                                onChange={(v) => updateGroupLocal(i, 'name', v)}
                                onSelect={(rec) => pickContactForGroupMember(i, rec)}
                                placeholder="Search or type a name…"
                                testId={`group-name-${i}`}
                              />
                            )}
                          </div>
                          <div><Label className="text-xs">DOB</Label><Input type="date" className="h-8 text-sm" value={m.dob || ''} onChange={e => updateGroupLocal(i, 'dob', e.target.value)} onBlur={() => saveGroupMembers(tour.group_members)} /></div>
                          <div><Label className="text-xs">Anniversary</Label><Input type="date" className="h-8 text-sm" value={m.anniversary || ''} onChange={e => updateGroupLocal(i, 'anniversary', e.target.value)} onBlur={() => saveGroupMembers(tour.group_members)} data-testid={`group-anniv-${i}`} /></div>
                          <div><Label className="text-xs">Phone</Label><Input type="tel" className="h-8 text-sm" value={m.phone || ''} onChange={e => updateGroupLocal(i, 'phone', e.target.value)} onBlur={() => saveGroupMembers(tour.group_members)} placeholder="+91…" data-testid={`group-phone-${i}`} /></div>
                          <div><Label className="text-xs">Notes</Label><Input className="h-8 text-sm" value={m.notes || ''} onChange={e => updateGroupLocal(i, 'notes', e.target.value)} onBlur={() => saveGroupMembers(tour.group_members)} placeholder="Room share / diet" /></div>
                        </div>
                        {/* Row 2 — relation + travel window. Relation lets the
                            operator promote a co-traveller into the customer's
                            family_members in one click. Travel window is for
                            partial-trip joiners; blank = full trip. */}
                        <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                          <div className="sm:col-span-2">
                            <Label className="text-xs">Relation (optional)</Label>
                            <Input
                              list={`rel-list-${i}`}
                              className="h-8 text-sm"
                              value={m.relation || ''}
                              onChange={e => updateGroupLocal(i, 'relation', e.target.value)}
                              onBlur={() => saveGroupMembers(tour.group_members)}
                              placeholder="e.g. Daughter, Spouse, Friend"
                              data-testid={`group-rel-${i}`}
                            />
                            <datalist id={`rel-list-${i}`}>
                              <option value="Spouse" />
                              <option value="Father" />
                              <option value="Mother" />
                              <option value="Son" />
                              <option value="Daughter" />
                              <option value="Brother" />
                              <option value="Sister" />
                              <option value="Friend" />
                              <option value="Co-traveller" />
                            </datalist>
                          </div>
                          <div>
                            <Label className="text-xs">Joined from</Label>
                            <Input
                              type="date"
                              className="h-8 text-sm"
                              value={m.joined_from || ''}
                              onChange={e => updateGroupLocal(i, 'joined_from', e.target.value)}
                              onBlur={() => saveGroupMembers(tour.group_members)}
                              min={tour.start_date || undefined}
                              max={tour.end_date || undefined}
                              data-testid={`group-from-${i}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Until</Label>
                            <Input
                              type="date"
                              className="h-8 text-sm"
                              value={m.joined_until || ''}
                              onChange={e => updateGroupLocal(i, 'joined_until', e.target.value)}
                              onBlur={() => saveGroupMembers(tour.group_members)}
                              min={tour.start_date || undefined}
                              max={tour.end_date || undefined}
                              data-testid={`group-until-${i}`}
                            />
                          </div>
                          <div className="sm:col-span-2 flex items-end pb-1">
                            <span className="text-[10px] text-muted-foreground">
                              {(!m.joined_from && !m.joined_until)
                                ? '✓ Full trip (default)'
                                : 'Partial trip'}
                            </span>
                          </div>
                        </div>
                        {/* Linking — if this co-traveller is (or later becomes) a Pollen Dots
                            customer, linking them here makes this tour's hotels/places/drivers/
                            guides automatically appear on THEIR share map. */}
                        <div className="flex items-center justify-between gap-2 pt-1 border-t">
                          {m.customer_id ? (
                            <div className="flex items-center gap-1.5 text-xs" data-testid={`linked-badge-${i}`}>
                              <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                                <LinkIcon className="w-3 h-3" />Linked to customer
                              </span>
                              <button type="button" onClick={() => navigate(`/pollen-dots/customer/${m.customer_id}`)} className="text-primary hover:underline">Open profile</button>
                            </div>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">Not a customer yet? Skip. Once they become one, come back and link.</span>
                          )}
                          <div className="flex gap-1">
                            <CustomerLinkPicker
                              linkedId={m.customer_id}
                              excludeId={tour.customer_id}
                              onPick={(c) => linkGroupMember(i, c)}
                            />
                            {m.customer_id && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => unlinkGroupMember(i)} data-testid={`unlink-btn-${i}`}>Unlink</Button>
                            )}
                          </div>
                        </div>
                        </div>
                        )}
                      </div>
                    );})}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dots of Care delivered on this tour */}
            <CareMomentsCard tourId={tour.id} customerId={tour.customer_id} customerName={tour.customer_name} />

            {data.inclusions || data.exclusions || editing ? (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Inclusions / Exclusions</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {editing ? (
                    <>
                      <div><Label className="text-xs">Inclusions</Label><Textarea className="text-sm" rows={2} value={editData.inclusions} onChange={e => updateEdit('inclusions', e.target.value)} /></div>
                      <div><Label className="text-xs">Exclusions</Label><Textarea className="text-sm" rows={2} value={editData.exclusions} onChange={e => updateEdit('exclusions', e.target.value)} /></div>
                    </>
                  ) : (
                    <>
                      {data.inclusions && <div><p className="text-xs font-medium text-muted-foreground mb-1">Inclusions</p><p className="text-sm whitespace-pre-wrap">{data.inclusions}</p></div>}
                      {data.exclusions && <div><p className="text-xs font-medium text-muted-foreground mb-1">Exclusions</p><p className="text-sm whitespace-pre-wrap">{data.exclusions}</p></div>}
                    </>
                  )}
                </CardContent>
              </Card>
            ) : null}
          </TabsContent>

          {/* Itinerary Tab */}
          <TabsContent value="itinerary" className="mt-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CalendarDays className="w-4 h-4" />Day-wise Itinerary</CardTitle></CardHeader>
              <CardContent>
                {(data.itinerary || []).length === 0 ? <p className="text-sm text-muted-foreground">No itinerary generated</p> : (
                  <div className="space-y-3">
                    {data.itinerary.map((day, i) => {
                      const d = day.date;
                      const dayHotels = (data.hotels || []).filter(h => (h.check_in && h.check_in <= d && h.check_out && h.check_out >= d));
                      const dayCheckins = (data.hotels || []).filter(h => h.check_in === d);
                      const dayCheckouts = (data.hotels || []).filter(h => h.check_out === d);
                      const dayActivities = (data.activities || []).filter(a => a.date === d);
                      const dayTransport = (data.transport || []).filter(t => {
                        const ts = t.start_date || t.date || '';
                        const te = t.end_date || ts;
                        return ts && ts <= d && te >= d;
                      });
                      const dayGuides = (data.guides || []).filter(g => {
                        const gs = g.start_date || '';
                        const ge = g.end_date || gs;
                        return gs && gs <= d && ge >= d;
                      });
                      // Day cards stay collapsed by default in edit mode so
                      // the operator scrolls a compact stack of dated rows
                      // and only expands the day they're working on. Dates
                      // drive order — no drag handle here.
                      const dayKey = `day-${i}`;
                      const dayOpen = !editing || expandedCard.days === dayKey;
                      const summary = [
                        day.overnight_location && `🛏 ${day.overnight_location}`,
                        dayCheckins.length && `${dayCheckins.length} check-in`,
                        dayCheckouts.length && `${dayCheckouts.length} check-out`,
                        dayActivities.length && `${dayActivities.length} activity`,
                        dayTransport.length && `${dayTransport.length} transport`,
                      ].filter(Boolean).join(' · ');
                      return (
                        <div key={i} className="rounded-lg border bg-muted/20">
                          <button
                            type="button"
                            onClick={() => editing && toggleExpand('days', dayKey)}
                            className={`w-full flex items-center gap-2 p-3 text-left ${editing ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default'}`}
                            data-testid={`day-toggle-${i}`}
                          >
                            <Badge className="bg-primary text-white min-w-[55px] justify-center text-xs">Day {day.day_number}</Badge>
                            <span className="text-sm font-medium">{formatDate(day.date)}</span>
                            <span className="text-xs text-muted-foreground">({day.weekday?.slice(0, 3)})</span>
                            {editing && !dayOpen && summary && (
                              <span className="text-[11px] text-muted-foreground truncate flex-1 ml-2">{summary}</span>
                            )}
                            {editing && (
                              <span className="ml-auto text-xs text-muted-foreground">{dayOpen ? '▾' : '▸'}</span>
                            )}
                          </button>
                          {dayOpen && (
                          <div className="p-3 pt-0 space-y-2">
                          {/* Overnight location */}
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-muted-foreground shrink-0">Overnight:</span>
                            {editing ? (
                              <div className="flex items-center gap-1 w-full sm:flex-1 sm:w-auto basis-full sm:basis-0 min-w-0">
                                <LocationAutocomplete value={day.overnight_location || ''} type="city" placeholder="Overnight location"
                                  className="h-6 text-xs flex-1 min-w-0"
                                  onChange={v => {
                                    const itin = [...editData.itinerary]; itin[i] = { ...itin[i], overnight_location: v };
                                    updateEdit('itinerary', itin);
                                  }}
                                  onSelect={async loc => {
                                    // Carry the picked location's coords +
                                    // ID forward so the save handler hydrates
                                    // an existing pollen_locations row instead
                                    // of creating a duplicate. If the city
                                    // already has verified coords on file we
                                    // also re-stamp them onto its own pin so
                                    // the map renders immediately — earlier
                                    // an unpinned-yet-known city would still
                                    // show "Pin" with no marker.
                                    const itin = [...editData.itinerary];
                                    itin[i] = {
                                      ...itin[i],
                                      overnight_location: loc.name,
                                      overnight_lat: loc.lat,
                                      overnight_lng: loc.lng,
                                      overnight_location_id: loc.id,
                                    };
                                    updateEdit('itinerary', itin);
                                    if (loc.lat != null && loc.lng != null) {
                                      try {
                                        await inheritCityPin({
                                          cityLoc: loc, entityName: loc.name, entityType: 'city',
                                        });
                                      } catch { /* opportunistic */ }
                                    }
                                  }} />
                                {day.overnight_location && <LocationPinBtn locationName={day.overnight_location} />}
                              </div>
                            ) : <span className="text-xs font-medium">{day.overnight_location || '-'}</span>}
                          </div>
                          {/* Enroute stops — multi-row editor in edit mode, compact list in view mode. */}
                          <DayStopsEditor
                            day={day}
                            dayIndex={i}
                            editing={editing}
                            editData={editData}
                            updateEdit={updateEdit}
                          />
                          {/* Day-wise Activity selector — search existing Activity
                              vendors or create a new one inline. Selecting a vendor
                              attaches its initiatives so the share map can
                              render the "Pollen Dot of Care" halo. */}
                          <DayActivitiesEditor
                            dayDate={d}
                            editing={editing}
                            editData={editData}
                            updateEdit={updateEdit}
                            vendors={vendors}
                          />
                          {/* Day events */}
                          <div className="space-y-1 pl-2 border-l-2 border-primary/20">
                            {dayCheckins.map((h, j) => (
                              <div key={`ci-${j}`} className="flex items-center gap-2 text-xs"><span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" /><span className="text-emerald-700 font-medium">Check-in:</span><span>{h.hotel_name}{h.room_type ? ` (${h.room_type})` : ''}</span></div>
                            ))}
                            {dayCheckouts.map((h, j) => (
                              <div key={`co-${j}`} className="flex items-center gap-2 text-xs"><span className="w-2 h-2 rounded-full bg-red-500 shrink-0" /><span className="text-red-700 font-medium">Check-out:</span><span>{h.hotel_name}</span></div>
                            ))}
                            {dayTransport.map((t, j) => (
                              <div key={`tr-${j}`} className="flex items-center gap-2 text-xs"><span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" /><span className="text-amber-700 font-medium">Transport:</span><span>{t.vehicle_type}{t.route ? ` — ${t.route}` : ''}{t.operator_name ? ` (${t.operator_name})` : ''}</span></div>
                            ))}
                            {dayActivities.map((a, j) => (
                              <div key={`ac-${j}`} className="flex items-center gap-2 text-xs"><span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" /><span className="text-blue-700 font-medium">Activity:</span><span>{a.activity_name}{a.time_slot ? ` (${a.time_slot})` : ''}</span></div>
                            ))}
                            {dayGuides.map((g, j) => (
                              <div key={`gu-${j}`} className="flex items-center gap-2 text-xs"><span className="w-2 h-2 rounded-full bg-teal-500 shrink-0" /><span className="text-teal-700 font-medium">Guide:</span><span>{g.guide_name}{g.location ? ` — ${g.location}` : ''}{g.phone ? ` (${g.phone})` : ''}</span></div>
                            ))}
                            {dayHotels.filter(h => h.check_in !== d && h.check_out !== d).map((h, j) => (
                              <div key={`st-${j}`} className="flex items-center gap-2 text-xs"><span className="w-2 h-2 rounded-full bg-gray-400 shrink-0" /><span className="text-muted-foreground">Stay:</span><span>{h.hotel_name}</span></div>
                            ))}
                            {dayCheckins.length === 0 && dayCheckouts.length === 0 && dayTransport.length === 0 && dayActivities.length === 0 && dayGuides.length === 0 && dayHotels.filter(h => h.check_in !== d && h.check_out !== d).length === 0 && (
                              <div className="text-xs text-muted-foreground italic">No events scheduled</div>
                            )}
                          </div>
                          </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="space-y-4 mt-4">
            {/* Hotels */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><Bed className="w-4 h-4" />Hotels ({(data.hotels || []).length})</CardTitle>
                  {editing && <div className="flex gap-2">
                    {(editData.hotels || []).length > 1 && <Button variant="outline" size="sm" onClick={sortByDate('hotels')} data-testid="sort-hotels" title="Sort hotels by check-in date"><ArrowDownUp className="w-3 h-3 mr-1" />Sort by date</Button>}
                    {(data.hotels || []).length > 0 && <Button variant="outline" size="sm" className="text-destructive" onClick={() => updateEdit('hotels', [])} data-testid="clear-hotels"><Trash2 className="w-3 h-3 mr-1" />Clear All</Button>}
                    <Button variant="outline" size="sm" onClick={() => addEditItem('hotels', { hotel_name: '', location: '', check_in: '', check_out: '', nights: 0, room_type: '', meal_plan: '', special_notes: '', contact_name: '', contact_number: '', booking_status: 'Pending', total_amount: 0, advance_paid: 0, balance: 0 })}><Plus className="w-3 h-3 mr-1" />Add Hotel</Button>
                  </div>}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.hotels || []).length === 0 ? <p className="text-sm text-muted-foreground py-2 text-center">{editing ? 'Click "+ Add Hotel" above' : 'No hotels added. Click Edit to add.'}</p> : editing ? (
                  // Editing → collapsible & sortable. Only ONE card opens at
                  // a time. Drag the grip on the left to reorder.
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder('hotels')}>
                    <SortableContext items={(editData.hotels || []).map((h, i) => h._cid || `hotel-${i}`)} strategy={verticalListSortingStrategy}>
                      {(editData.hotels || []).map((h, i) => {
                        const cid = h._cid || `hotel-${i}`;
                        const open = expandedCard.hotels === cid;
                        const inLine = [h.location, formatDate(h.check_in), h.check_out && `→ ${formatDate(h.check_out)}`]
                          .filter(Boolean).join(' · ');
                        return (
                          <SortableCollapsibleCard
                            key={cid}
                            id={cid}
                            index={i}
                            title={h.hotel_name || `Hotel ${i + 1}`}
                            subtitle={inLine || 'Tap to add details'}
                            expanded={open}
                            onToggle={() => toggleExpand('hotels', cid)}
                            onRemove={() => removeEditItem('hotels', i)}
                            badge={h.booking_status && (
                              <Badge variant="outline" className="text-[10px] shrink-0">{h.booking_status}</Badge>
                            )}
                            testid={`hotel-card-${i}`}
                          >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div><Label className="text-xs">Hotel Name</Label><VendorAutocomplete value={editData.hotels[i]?.hotel_name || ''} vendors={vendors.filter(v => v.type === 'Hotel')} placeholder="Type or select hotel"
                              onChange={v => updateEditList('hotels', i, 'hotel_name', v)}
                              onSelect={async v => {
                                // Carry every known field forward — name,
                                // contact + phone, AND the vendor's stored
                                // city/location so a fresh hotel row picks
                                // up coords from the locations DB on the
                                // very next render. Earlier this only
                                // copied name + contact and left the
                                // operator to retype the city + click Pin.
                                updateEditList('hotels', i, 'hotel_name', v.name);
                                if (v.contact_name) updateEditList('hotels', i, 'contact_name', v.contact_name);
                                if (v.phone) updateEditList('hotels', i, 'contact_number', v.phone);
                                const existingLoc = (editData.hotels[i]?.location || '').trim();
                                const vendorLoc = (v.location || v.city || '').trim();
                                if (!existingLoc && vendorLoc) {
                                  updateEditList('hotels', i, 'location', vendorLoc);
                                  // Auto-inherit the city pin onto this hotel so the
                                  // map icon resolves without the operator clicking Pin.
                                  try {
                                    const t = localStorage.getItem('session_token');
                                    const r = await fetch(
                                      `${API}/pollen/locations?type=city&q=${encodeURIComponent(vendorLoc)}&limit=1`,
                                      { credentials: 'include',
                                        headers: t ? { Authorization: `Bearer ${t}` } : {} },
                                    );
                                    if (r.ok) {
                                      const arr = await r.json();
                                      const cityLoc = Array.isArray(arr) ? arr[0] : null;
                                      if (cityLoc && cityLoc.lat != null && cityLoc.lng != null) {
                                        const out = await inheritCityPin({
                                          cityLoc, entityName: v.name, entityType: 'hotel',
                                        });
                                        if (out.inherited) {
                                          toast.success(`Inherited ${cityLoc.name} pin onto "${v.name}".`);
                                        }
                                      }
                                    }
                                  } catch { /* opportunistic — silent on failure */ }
                                }
                              }} /></div>
                            <div>
                              <div className="flex items-center justify-between">
                                <Label className="text-xs">Location</Label>
                                {editData.hotels[i]?.location && <LocationPinBtn locationName={editData.hotels[i]?.location} />}
                              </div>
                              <LocationAutocomplete value={editData.hotels[i]?.location || ''} type="city" className="h-7 text-xs"
                              onChange={v => updateEditList('hotels', i, 'location', v)}
                              onSelect={async (loc) => {
                                updateEditList('hotels', i, 'location', loc.name);
                                const hn = editData.hotels[i]?.hotel_name;
                                const r = await inheritCityPin({ cityLoc: loc, entityName: hn, entityType: 'hotel' });
                                if (r.inherited) toast.success(`Inherited ${loc.name} pin onto "${hn}".`);
                              }} />
                            </div>
                            <div><Label className="text-xs">Check-in</Label><Input type="date" className="h-7 text-xs" value={editData.hotels[i]?.check_in || ''} onChange={e => updateEditList('hotels', i, 'check_in', e.target.value)} /></div>
                            <div><Label className="text-xs">Check-out</Label><Input type="date" className="h-7 text-xs" value={editData.hotels[i]?.check_out || ''} onChange={e => updateEditList('hotels', i, 'check_out', e.target.value)} /></div>
                            <div><Label className="text-xs">Room Type</Label><Input className="h-7 text-xs" value={editData.hotels[i]?.room_type || ''} onChange={e => updateEditList('hotels', i, 'room_type', e.target.value)} /></div>
                            <div><Label className="text-xs">Meal Plan</Label><Input className="h-7 text-xs" value={editData.hotels[i]?.meal_plan || ''} onChange={e => updateEditList('hotels', i, 'meal_plan', e.target.value)} /></div>
                            <div><Label className="text-xs">Contact Name</Label><Input className="h-7 text-xs" value={editData.hotels[i]?.contact_name || ''} onChange={e => updateEditList('hotels', i, 'contact_name', e.target.value)} /></div>
                            <div><Label className="text-xs">Contact Number</Label><Input className="h-7 text-xs" value={editData.hotels[i]?.contact_number || ''} onChange={e => updateEditList('hotels', i, 'contact_number', e.target.value)} /></div>
                            <div><Label className="text-xs">Status</Label>
                              <Select value={editData.hotels[i]?.booking_status || 'Pending'} onValueChange={v => updateEditList('hotels', i, 'booking_status', v)}>
                                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{['Pending', 'Booked', 'Confirmed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                            <div><Label className="text-xs">Total</Label><Input type="number" className="h-7 text-xs" value={editData.hotels[i]?.total_amount || 0} onChange={e => updateEditList('hotels', i, 'total_amount', e.target.value)} /></div>
                            <div><Label className="text-xs">Advance</Label><Input type="number" className="h-7 text-xs" value={editData.hotels[i]?.advance_paid || 0} onChange={e => updateEditList('hotels', i, 'advance_paid', e.target.value)} /></div>
                            <div><Label className="text-xs">Balance</Label><Input className="h-7 text-xs bg-muted" value={editData.hotels[i]?.balance || 0} readOnly /></div>
                          </div>
                          {/* Pin the exact hotel on the map — writes a verified
                              row into the shared pollen_locations DB the moment
                              the admin hits Verify. */}
                          {editData.hotels[i]?.hotel_name && (
                            <div className="pt-2 border-t">
                              <HotelMapUrlField
                                entityName={editData.hotels[i]?.hotel_name}
                                entityType="hotel"
                                city={editData.hotels[i]?.location}
                                initialUrl={editData.hotels[i]?.map_url}
                                initialLat={editData.hotels[i]?.lat}
                                initialLng={editData.hotels[i]?.lng}
                                initialVerified={editData.hotels[i]?.map_url ? true : false}
                                onPinned={(p) => {
                                  updateEditList('hotels', i, 'map_url', p.url);
                                  updateEditList('hotels', i, 'lat', p.lat);
                                  updateEditList('hotels', i, 'lng', p.lng);
                                }}
                                compact
                                label="Google Maps URL (for map pin)"
                              />
                            </div>
                          )}
                          {/* Tour-level "Pollen Dot of Care" on the hotel.
                              Notes show in the hotel popup as the care
                              narrative; initiative pills tag the dot
                              with brand initiatives. Both stay on this
                              tour only — never overwrite vendor data. */}
                          <div className="pt-2 border-t space-y-1.5">
                            <Label className="text-xs">Notes for this stay (shows in hotel popup as Pollen Dot of Care)</Label>
                            <Textarea
                              rows={3}
                              className="text-xs"
                              value={editData.hotels[i]?.notes || ''}
                              onChange={e => updateEditList('hotels', i, 'notes', e.target.value)}
                              placeholder="e.g. Skiltang Heritage offered a warm and personal stay hosted by Rigzin and his family…"
                              data-testid={`hotel-notes-${i}`}
                            />
                            <div className="flex items-center gap-1 flex-wrap pt-1">
                              <Sparkles className="w-3 h-3 text-amber-600 shrink-0" />
                              <span className="text-[10px] text-muted-foreground mr-0.5">Pollen Dot of Care:</span>
                              {[
                                { key: 'smiles',            label: 'Smiles',            color: '#f59e0b', activeBg: 'bg-amber-500',  activeText: 'text-white', bg: 'bg-amber-50',  text: 'text-amber-800',  border: 'border-amber-200' },
                                { key: 'cultural-exchange', label: 'Cultural Exchange', color: '#c2410c', activeBg: 'bg-orange-700', activeText: 'text-white', bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200' },
                                { key: 'sustainability',    label: 'Sustainability',    color: '#84cc16', activeBg: 'bg-lime-600',   activeText: 'text-white', bg: 'bg-lime-50',   text: 'text-lime-800',   border: 'border-lime-200' },
                              ].map(it => {
                                const cur = editData.hotels[i]?.initiatives || [];
                                const on = cur.includes(it.key);
                                return (
                                  <button
                                    key={it.key}
                                    type="button"
                                    onClick={() => {
                                      const next = on ? cur.filter(k => k !== it.key) : [...cur, it.key];
                                      updateEditList('hotels', i, 'initiatives', next);
                                    }}
                                    title={on ? `Click to remove ${it.label}` : `Tag this stay as ${it.label}`}
                                    data-testid={`hotel-init-${it.key}-${i}`}
                                    className={`text-[10px] px-1.5 py-0.5 rounded-full border transition-colors leading-none ${on ? `${it.activeBg} ${it.activeText} border-transparent` : `${it.bg} ${it.text} ${it.border} hover:opacity-80`}`}
                                  >
                                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: on ? '#fff' : it.color }} />
                                    {it.label}
                                    {on && <span className="ml-1 opacity-80">×</span>}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          </SortableCollapsibleCard>
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                ) : (
                  // Read-only — keep the existing compact summary card so
                  // share-view of the tour stays unchanged.
                  data.hotels.map((h, i) => (
                    <div key={i} className="p-3 border rounded-lg space-y-2 relative">
                          <div className="flex items-center justify-between"><span className="font-medium text-sm">{h.hotel_name || 'Unnamed Hotel'}</span><Badge variant="outline" className="text-xs">{h.booking_status}</Badge></div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
                            <span><MapPin className="w-3 h-3 inline mr-1" />{h.location || '-'}</span><span>In: {formatDate(h.check_in)}</span><span>Out: {formatDate(h.check_out)}</span><span>{(() => { const n = nightsBetween(h.check_in, h.check_out); return `${n} ${n === 1 ? 'night' : 'nights'}`; })()}</span>
                          </div>
                          {h.contact_name && <div className="text-xs text-muted-foreground">Contact: {h.contact_name} {h.contact_number && `(${h.contact_number})`}</div>}
                          {parseFloat(h.total_amount || 0) > 0 && (() => {
                            const paid = sumPaidFor('hotel', h.vendor_id, h.hotel_name, h.advance_paid);
                            const total = parseFloat(h.total_amount || 0);
                            return (
                              <div className="flex gap-4 pt-2 border-t text-xs">
                                <span>Total: <strong>{total.toLocaleString('en-IN')}</strong></span>
                                <span className="text-emerald-600">Paid: {paid.toLocaleString('en-IN')}</span>
                                <span className="text-red-600">Balance: {Math.max(0, total - paid).toLocaleString('en-IN')}</span>
                              </div>
                            );
                          })()}
                          <ServicePayments tourId={data.id} category="hotel" vendorName={h.hotel_name} vendorId={h.vendor_id}
                            defaultAmount={Math.max(0, (parseFloat(h.total_amount || 0) - sumPaidFor('hotel', h.vendor_id, h.hotel_name, h.advance_paid)))}
                            onChanged={() => fetchTour && fetchTour()} />
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Activities */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><Compass className="w-4 h-4" />Activities ({(data.activities || []).length})</CardTitle>
                  {editing && <div className="flex gap-2">
                    {(editData.activities || []).length > 1 && <Button variant="outline" size="sm" onClick={sortByDate('activities')} data-testid="sort-activities" title="Sort activities by date"><ArrowDownUp className="w-3 h-3 mr-1" />Sort by date</Button>}
                    {(data.activities || []).length > 0 && <Button variant="outline" size="sm" className="text-destructive" onClick={() => updateEdit('activities', [])} data-testid="clear-activities"><Trash2 className="w-3 h-3 mr-1" />Clear All</Button>}
                    <Button variant="outline" size="sm" onClick={() => addEditItem('activities', { activity_name: '', date: '', time_slot: '', location: '', contact_details: '', status: 'Pending', total_amount: 0, advance: 0, balance: 0 })}><Plus className="w-3 h-3 mr-1" />Add Activity</Button>
                  </div>}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.activities || []).length === 0 ? <p className="text-sm text-muted-foreground py-2 text-center">{editing ? 'Click "+ Add Activity" above' : 'No activities added. Click Edit to add.'}</p> : editing ? (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder('activities')}>
                    <SortableContext items={(editData.activities || []).map((a, i) => a._cid || `activity-${i}`)} strategy={verticalListSortingStrategy}>
                      {(editData.activities || []).map((a, i) => {
                        const cid = a._cid || `activity-${i}`;
                        const open = expandedCard.activities === cid;
                        const inLine = [a.location, a.date && formatDate(a.date), a.time_slot]
                          .filter(Boolean).join(' · ');
                        return (
                          <SortableCollapsibleCard
                            key={cid}
                            id={cid}
                            index={i}
                            title={a.activity_name || `Activity ${i + 1}`}
                            subtitle={inLine || 'Tap to add details'}
                            expanded={open}
                            onToggle={() => toggleExpand('activities', cid)}
                            onRemove={() => removeEditItem('activities', i)}
                            badge={a.status && (
                              <Badge variant="outline" className="text-[10px] shrink-0">{a.status}</Badge>
                            )}
                            testid={`activity-card-${i}`}
                          >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div><Label className="text-xs">Activity Name</Label><VendorAutocomplete value={editData.activities[i]?.activity_name || ''} vendors={vendors.filter(v => v.type === 'Activity')} placeholder="Type or select activity"
                              onChange={v => updateEditList('activities', i, 'activity_name', v)}
                              onSelect={v => { updateEditList('activities', i, 'activity_name', v.name); if (v.address && !editData.activities[i]?.contact_details) updateEditList('activities', i, 'contact_details', v.address); if (v.location && !editData.activities[i]?.location) updateEditList('activities', i, 'location', v.location); }} /></div>
                            <div className="col-span-2"><Label className="text-xs">Location</Label><LocationAutocomplete value={editData.activities[i]?.location || ''} type="city" className="h-7 text-xs"
                              onChange={v => updateEditList('activities', i, 'location', v)}
                              onSelect={async (loc) => {
                                updateEditList('activities', i, 'location', loc.name);
                                const an = editData.activities[i]?.activity_name;
                                const r = await inheritCityPin({ cityLoc: loc, entityName: an, entityType: 'activity' });
                                if (r.inherited) toast.success(`Inherited ${loc.name} pin onto "${an}".`);
                              }} /></div>
                            {editData.activities[i]?.activity_name && (
                              <div className="col-span-2">
                                <HotelMapUrlField
                                  entityName={editData.activities[i]?.activity_name}
                                  entityType="activity"
                                  city={editData.activities[i]?.location}
                                  initialUrl={editData.activities[i]?.maps_url || editData.activities[i]?.map_url}
                                  initialLat={editData.activities[i]?.lat}
                                  initialLng={editData.activities[i]?.lng}
                                  initialVerified={!!(editData.activities[i]?.maps_url || editData.activities[i]?.map_url)}
                                  onPinned={(p) => {
                                    updateEditList('activities', i, 'maps_url', p.url);
                                    updateEditList('activities', i, 'lat', p.lat);
                                    updateEditList('activities', i, 'lng', p.lng);
                                  }}
                                  compact
                                  label="Google Maps URL (paste link to pin exact spot)"
                                />
                              </div>
                            )}
                            <div><Label className="text-xs">Date</Label><Input type="date" className="h-7 text-xs" value={editData.activities[i]?.date || ''} onChange={e => updateEditList('activities', i, 'date', e.target.value)} /></div>
                            <div><Label className="text-xs">Time Slot</Label><Input className="h-7 text-xs" value={editData.activities[i]?.time_slot || ''} onChange={e => updateEditList('activities', i, 'time_slot', e.target.value)} /></div>
                            <div><Label className="text-xs">Contact</Label><Input className="h-7 text-xs" value={editData.activities[i]?.contact_details || ''} onChange={e => updateEditList('activities', i, 'contact_details', e.target.value)} /></div>
                            <div><Label className="text-xs">Status</Label>
                              <Select value={editData.activities[i]?.status || 'Pending'} onValueChange={v => updateEditList('activities', i, 'status', v)}>
                                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{['Pending', 'Booked', 'Confirmed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                            <div><Label className="text-xs">Total</Label><Input type="number" className="h-7 text-xs" value={editData.activities[i]?.total_amount || 0} onChange={e => updateEditList('activities', i, 'total_amount', e.target.value)} /></div>
                            <div><Label className="text-xs">Advance</Label><Input type="number" className="h-7 text-xs" value={editData.activities[i]?.advance || 0} onChange={e => updateEditList('activities', i, 'advance', e.target.value)} /></div>
                            <div><Label className="text-xs">Balance</Label><Input className="h-7 text-xs bg-muted" value={editData.activities[i]?.balance || 0} readOnly /></div>
                          </div>
                          {/* Tour-level "Pollen Dot of Care" on the activity.
                              Notes show in the activity popup as the care
                              narrative; initiative pills tag the dot
                              with brand initiatives. Both stay on this
                              tour only — never overwrite vendor data. */}
                          <div className="pt-2 border-t space-y-1.5">
                            <Label className="text-xs">Notes for this activity (shows in activity popup as Pollen Dot of Care)</Label>
                            <Textarea
                              rows={3}
                              className="text-xs"
                              value={editData.activities[i]?.notes || ''}
                              onChange={e => updateEditList('activities', i, 'notes', e.target.value)}
                              placeholder="e.g. Spent the morning weaving with Jirma's collective…"
                              data-testid={`activity-notes-${i}`}
                            />
                            <div className="flex items-center gap-1 flex-wrap pt-1">
                              <Sparkles className="w-3 h-3 text-amber-600 shrink-0" />
                              <span className="text-[10px] text-muted-foreground mr-0.5">Pollen Dot of Care:</span>
                              {[
                                { key: 'smiles',            label: 'Smiles',            color: '#f59e0b', activeBg: 'bg-amber-500',  activeText: 'text-white', bg: 'bg-amber-50',  text: 'text-amber-800',  border: 'border-amber-200' },
                                { key: 'cultural-exchange', label: 'Cultural Exchange', color: '#c2410c', activeBg: 'bg-orange-700', activeText: 'text-white', bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200' },
                                { key: 'sustainability',    label: 'Sustainability',    color: '#84cc16', activeBg: 'bg-lime-600',   activeText: 'text-white', bg: 'bg-lime-50',   text: 'text-lime-800',   border: 'border-lime-200' },
                              ].map(it => {
                                const cur = editData.activities[i]?.initiatives || [];
                                const on = cur.includes(it.key);
                                return (
                                  <button
                                    key={it.key}
                                    type="button"
                                    onClick={() => {
                                      const next = on ? cur.filter(k => k !== it.key) : [...cur, it.key];
                                      updateEditList('activities', i, 'initiatives', next);
                                    }}
                                    title={on ? `Click to remove ${it.label}` : `Tag this activity as ${it.label}`}
                                    data-testid={`activity-init-${it.key}-${i}`}
                                    className={`text-[10px] px-1.5 py-0.5 rounded-full border transition-colors leading-none ${on ? `${it.activeBg} ${it.activeText} border-transparent` : `${it.bg} ${it.text} ${it.border} hover:opacity-80`}`}
                                  >
                                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: on ? '#fff' : it.color }} />
                                    {it.label}
                                    {on && <span className="ml-1 opacity-80">×</span>}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          </SortableCollapsibleCard>
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                ) : (
                  data.activities.map((a, i) => (
                    <div key={i} className="p-3 border rounded-lg space-y-2 relative">
                          <div className="flex items-center justify-between"><span className="font-medium text-sm">{a.activity_name || 'Activity'}</span><Badge variant="outline" className="text-xs">{a.status}</Badge></div>
                          <div className="flex gap-3 text-xs text-muted-foreground"><span><CalendarDays className="w-3 h-3 inline mr-1" />{formatDate(a.date)}</span>{a.time_slot && <span><Clock className="w-3 h-3 inline mr-1" />{a.time_slot}</span>}{a.location && <span><MapPin className="w-3 h-3 inline mr-1" />{a.location}</span>}{a.contact_details && <span>{a.contact_details}</span>}</div>
                          {parseFloat(a.total_amount || 0) > 0 && (() => {
                            const paid = sumPaidFor('activity', a.vendor_id, a.activity_name, a.advance);
                            const total = parseFloat(a.total_amount || 0);
                            return (
                              <div className="flex gap-4 pt-1 text-xs">
                                <span>Total: <strong>{total.toLocaleString('en-IN')}</strong></span>
                                <span className="text-emerald-600">Paid: {paid.toLocaleString('en-IN')}</span>
                                <span className="text-red-600">Balance: {Math.max(0, total - paid).toLocaleString('en-IN')}</span>
                              </div>
                            );
                          })()}
                          <ServicePayments tourId={data.id} category="activity" vendorName={a.activity_name} vendorId={a.vendor_id}
                            defaultAmount={Math.max(0, (parseFloat(a.total_amount || 0) - parseFloat(a.advance || 0)))}
                            onChanged={() => fetchTour && fetchTour()} />
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Transport */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><Bus className="w-4 h-4" />Transport ({(data.transport || []).length})</CardTitle>
                  {editing && <div className="flex gap-2">
                    {(editData.transport || []).length > 1 && <Button variant="outline" size="sm" onClick={sortByDate('transport')} data-testid="sort-transport" title="Sort transport by start date"><ArrowDownUp className="w-3 h-3 mr-1" />Sort by date</Button>}
                    {(data.transport || []).length > 0 && <Button variant="outline" size="sm" className="text-destructive" onClick={() => updateEdit('transport', [])} data-testid="clear-transport"><Trash2 className="w-3 h-3 mr-1" />Clear All</Button>}
                    <Button variant="outline" size="sm" onClick={() => addEditItem('transport', { vehicle_type: '', route: '', operator_name: '', operator_contact: '', driver_name: '', driver_contact: '', start_date: '', end_date: '', status: 'Pending', total_amount: 0, advance: 0, balance: 0 })}><Plus className="w-3 h-3 mr-1" />Add Transport</Button>
                  </div>}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.transport || []).length === 0 ? <p className="text-sm text-muted-foreground py-2 text-center">{editing ? 'Click "+ Add Transport" above' : 'No transport added. Click Edit to add.'}</p> : editing ? (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder('transport')}>
                    <SortableContext items={(editData.transport || []).map((t, i) => t._cid || `transport-${i}`)} strategy={verticalListSortingStrategy}>
                      {(editData.transport || []).map((t, i) => {
                        const cid = t._cid || `transport-${i}`;
                        const open = expandedCard.transport === cid;
                        const inLine = [t.vehicle_type, t.route, t.start_date && formatDate(t.start_date)]
                          .filter(Boolean).join(' · ');
                        return (
                          <SortableCollapsibleCard
                            key={cid}
                            id={cid}
                            index={i}
                            title={t.operator_name || t.vehicle_type || `Transport ${i + 1}`}
                            subtitle={inLine || 'Tap to add details'}
                            expanded={open}
                            onToggle={() => toggleExpand('transport', cid)}
                            onRemove={() => removeEditItem('transport', i)}
                            badge={t.status && (
                              <Badge variant="outline" className="text-[10px] shrink-0">{t.status}</Badge>
                            )}
                            testid={`transport-card-${i}`}
                          >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div><Label className="text-xs">Vehicle Type</Label><Input className="h-7 text-xs" value={editData.transport[i]?.vehicle_type || ''} onChange={e => updateEditList('transport', i, 'vehicle_type', e.target.value)} /></div>
                            <div>
                              <Label className="text-xs">Route</Label>
                              <Input
                                className="h-7 text-xs"
                                placeholder="e.g. Chandigarh → Amritsar → Shimla"
                                value={editData.transport[i]?.route || ''}
                                onChange={e => updateEditList('transport', i, 'route', e.target.value)}
                                data-testid={`transport-route-${i}`}
                              />
                            </div>
                            <div><Label className="text-xs">Start Date</Label><Input type="date" className="h-7 text-xs" value={editData.transport[i]?.start_date || ''} onChange={e => updateEditList('transport', i, 'start_date', e.target.value)} /></div>
                            <div><Label className="text-xs">End Date</Label><Input type="date" className="h-7 text-xs" value={editData.transport[i]?.end_date || ''} onChange={e => updateEditList('transport', i, 'end_date', e.target.value)} /></div>
                            <div><Label className="text-xs">Operator</Label><VendorAutocomplete value={editData.transport[i]?.operator_name || ''} vendors={vendors.filter(v => v.type === 'Transport')} placeholder="Type or select operator"
                              onChange={v => updateEditList('transport', i, 'operator_name', v)}
                              onSelect={v => { updateEditList('transport', i, 'operator_name', v.name); if (v.phone) updateEditList('transport', i, 'operator_contact', v.phone); }} /></div>
                            <div><Label className="text-xs">Operator Contact</Label><Input className="h-7 text-xs" value={editData.transport[i]?.operator_contact || ''} onChange={e => updateEditList('transport', i, 'operator_contact', e.target.value)} /></div>
                            <div><Label className="text-xs">Driver</Label><VendorAutocomplete value={editData.transport[i]?.driver_name || ''} vendors={vendors.filter(v => v.type === 'Driver')} placeholder="Type or select driver"
                              onChange={v => updateEditList('transport', i, 'driver_name', v)}
                              onSelect={v => { updateEditList('transport', i, 'driver_name', v.name); if (v.phone) updateEditList('transport', i, 'driver_contact', v.phone); }} /></div>
                            <div><Label className="text-xs">Driver Contact</Label><Input className="h-7 text-xs" value={editData.transport[i]?.driver_contact || ''} onChange={e => updateEditList('transport', i, 'driver_contact', e.target.value)} /></div>
                            <div><Label className="text-xs">Status</Label>
                              <Select value={editData.transport[i]?.status || 'Pending'} onValueChange={v => updateEditList('transport', i, 'status', v)}>
                                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{['Pending', 'Booked', 'Confirmed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                            <div><Label className="text-xs">Total</Label><Input type="number" className="h-7 text-xs" value={editData.transport[i]?.total_amount || 0} onChange={e => updateEditList('transport', i, 'total_amount', e.target.value)} /></div>
                            <div><Label className="text-xs">Advance</Label><Input type="number" className="h-7 text-xs" value={editData.transport[i]?.advance || 0} onChange={e => updateEditList('transport', i, 'advance', e.target.value)} /></div>
                            <div><Label className="text-xs">Balance</Label><Input className="h-7 text-xs bg-muted" value={editData.transport[i]?.balance || 0} readOnly /></div>
                          </div>
                          </SortableCollapsibleCard>
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                ) : (
                  data.transport.map((t, i) => (
                    <div key={i} className="p-3 border rounded-lg space-y-2 relative">
                          <div className="flex items-center justify-between"><span className="font-medium text-sm">{t.vehicle_type || 'Transport'} — {t.route || '-'}</span><Badge variant="outline" className="text-xs">{t.status || 'Pending'}</Badge></div>
                          <div className="flex gap-3 text-xs text-muted-foreground">{(t.start_date || t.date) && <span><CalendarDays className="w-3 h-3 inline mr-1" />{formatDate(t.start_date || t.date)}{t.end_date ? ` to ${formatDate(t.end_date)}` : ''}</span>}{t.operator_name && <span>{t.operator_name}</span>}{t.driver_name && <span>Driver: {t.driver_name}</span>}</div>
                          {parseFloat(t.total_amount || 0) > 0 && (() => {
                            const paid = sumPaidFor('transport', t.vendor_id, t.operator_name || t.vehicle_type, t.advance);
                            const total = parseFloat(t.total_amount || 0);
                            return (
                              <div className="flex gap-4 pt-1 text-xs">
                                <span>Total: <strong>{total.toLocaleString('en-IN')}</strong></span>
                                <span className="text-emerald-600">Paid: {paid.toLocaleString('en-IN')}</span>
                                <span className="text-red-600">Balance: {Math.max(0, total - paid).toLocaleString('en-IN')}</span>
                              </div>
                            );
                          })()}
                          <ServicePayments tourId={data.id} category="transport" vendorName={t.operator_name || t.vehicle_type} vendorId={t.vendor_id}
                            defaultAmount={Math.max(0, (parseFloat(t.total_amount || 0) - parseFloat(t.advance || 0)))}
                            onChanged={() => fetchTour && fetchTour()} />
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Guides */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><MapPin className="w-4 h-4" />Guides ({(data.guides || []).length})</CardTitle>
                  {editing && <div className="flex gap-2">
                    {(editData.guides || []).length > 1 && <Button variant="outline" size="sm" onClick={sortByDate('guides')} data-testid="sort-guides" title="Sort guides by start date"><ArrowDownUp className="w-3 h-3 mr-1" />Sort by date</Button>}
                    {(data.guides || []).length > 0 && <Button variant="outline" size="sm" className="text-destructive" onClick={() => updateEdit('guides', [])} data-testid="clear-guides"><Trash2 className="w-3 h-3 mr-1" />Clear All</Button>}
                    <Button variant="outline" size="sm" onClick={() => addEditItem('guides', { guide_name: '', phone: '', email: '', location: '', start_date: '', end_date: '', status: 'Pending', total_amount: 0, advance: 0, balance: 0 })}><Plus className="w-3 h-3 mr-1" />Add Guide</Button>
                  </div>}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.guides || []).length === 0 ? <p className="text-sm text-muted-foreground py-2 text-center">{editing ? 'Click "+ Add Guide" above' : 'No guides added. Click Edit to add.'}</p> : editing ? (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder('guides')}>
                    <SortableContext items={(editData.guides || []).map((g, i) => g._cid || `guide-${i}`)} strategy={verticalListSortingStrategy}>
                      {(editData.guides || []).map((g, i) => {
                        const cid = g._cid || `guide-${i}`;
                        const open = expandedCard.guides === cid;
                        const inLine = [g.location, g.start_date && formatDate(g.start_date), g.phone]
                          .filter(Boolean).join(' · ');
                        return (
                          <SortableCollapsibleCard
                            key={cid}
                            id={cid}
                            index={i}
                            title={g.guide_name || `Guide ${i + 1}`}
                            subtitle={inLine || 'Tap to add details'}
                            expanded={open}
                            onToggle={() => toggleExpand('guides', cid)}
                            onRemove={() => removeEditItem('guides', i)}
                            badge={g.status && (
                              <Badge variant="outline" className="text-[10px] shrink-0">{g.status}</Badge>
                            )}
                            testid={`guide-card-${i}`}
                          >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div><Label className="text-xs">Guide Name</Label><VendorAutocomplete value={editData.guides[i]?.guide_name || ''} vendors={vendors.filter(v => v.type === 'Guide')} placeholder="Type or select guide"
                              onChange={v => updateEditList('guides', i, 'guide_name', v)}
                              onSelect={v => { updateEditList('guides', i, 'guide_name', v.name); if (v.phone) updateEditList('guides', i, 'phone', v.phone); if (v.email) updateEditList('guides', i, 'email', v.email); if (v.address && !editData.guides[i]?.location) updateEditList('guides', i, 'location', v.address); }} /></div>
                            <div><Label className="text-xs">Phone</Label><Input className="h-7 text-xs" value={editData.guides[i]?.phone || ''} onChange={e => updateEditList('guides', i, 'phone', e.target.value)} /></div>
                            <div><Label className="text-xs">Email</Label><Input className="h-7 text-xs" value={editData.guides[i]?.email || ''} onChange={e => updateEditList('guides', i, 'email', e.target.value)} /></div>
                            <div>
                              <div className="flex items-center justify-between">
                                <Label className="text-xs">Location</Label>
                                {editData.guides[i]?.location && <LocationPinBtn locationName={editData.guides[i]?.location} />}
                              </div>
                              <LocationAutocomplete value={editData.guides[i]?.location || ''} type="city" className="h-7 text-xs"
                              onChange={v => updateEditList('guides', i, 'location', v)}
                              onSelect={async (loc) => {
                                updateEditList('guides', i, 'location', loc.name);
                                const gn = editData.guides[i]?.guide_name;
                                const r = await inheritCityPin({ cityLoc: loc, entityName: gn, entityType: 'guide' });
                                if (r.inherited) toast.success(`Inherited ${loc.name} pin onto "${gn}".`);
                              }} />
                            </div>
                            <div><Label className="text-xs">Start Date</Label><Input type="date" className="h-7 text-xs" value={editData.guides[i]?.start_date || ''} onChange={e => updateEditList('guides', i, 'start_date', e.target.value)} /></div>
                            <div><Label className="text-xs">End Date</Label><Input type="date" className="h-7 text-xs" value={editData.guides[i]?.end_date || ''} onChange={e => updateEditList('guides', i, 'end_date', e.target.value)} /></div>
                            <div><Label className="text-xs">Status</Label>
                              <Select value={editData.guides[i]?.status || 'Pending'} onValueChange={v => updateEditList('guides', i, 'status', v)}>
                                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{['Pending', 'Booked', 'Confirmed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                            <div><Label className="text-xs">Total</Label><Input type="number" className="h-7 text-xs" value={editData.guides[i]?.total_amount || 0} onChange={e => updateEditList('guides', i, 'total_amount', e.target.value)} /></div>
                            <div><Label className="text-xs">Advance</Label><Input type="number" className="h-7 text-xs" value={editData.guides[i]?.advance || 0} onChange={e => updateEditList('guides', i, 'advance', e.target.value)} /></div>
                            <div><Label className="text-xs">Balance</Label><Input className="h-7 text-xs bg-muted" value={editData.guides[i]?.balance || 0} readOnly /></div>
                          </div>
                          </SortableCollapsibleCard>
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                ) : (
                  data.guides.map((g, i) => (
                    <div key={i} className="p-3 border rounded-lg space-y-2 relative">
                          <div className="flex items-center justify-between"><span className="font-medium text-sm">{g.guide_name || 'Guide'}</span><Badge variant="outline" className="text-xs">{g.status || 'Pending'}</Badge></div>
                          <div className="flex gap-3 text-xs text-muted-foreground">{g.location && <span><MapPin className="w-3 h-3 inline mr-1" />{g.location}</span>}{g.start_date && <span><CalendarDays className="w-3 h-3 inline mr-1" />{formatDate(g.start_date)}{g.end_date ? ` to ${formatDate(g.end_date)}` : ''}</span>}{g.phone && <span>{g.phone}</span>}</div>
                          {parseFloat(g.total_amount || 0) > 0 && <div className="flex gap-4 pt-1 text-xs"><span>Total: <strong>{parseFloat(g.total_amount || 0).toLocaleString('en-IN')}</strong></span><span className="text-emerald-600">Paid: {parseFloat(g.advance || 0).toLocaleString('en-IN')}</span><span className="text-red-600">Balance: {(parseFloat(g.total_amount || 0) - parseFloat(g.advance || 0)).toLocaleString('en-IN')}</span></div>}
                          <ServicePayments tourId={data.id} category="guide" vendorName={g.guide_name} vendorId={g.vendor_id}
                            defaultAmount={Math.max(0, (parseFloat(g.total_amount || 0) - parseFloat(g.advance || 0)))}
                            onChanged={() => fetchTour && fetchTour()} />
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Initiatives */}
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2"><Leaf className="w-4 h-4 text-emerald-600" />Initiatives ({(data.initiatives || []).length})</CardTitle>
                {editing && <div className="flex gap-2">
                  {(data.initiatives || []).length > 0 && <Button variant="outline" size="sm" className="text-destructive" onClick={() => updateEdit('initiatives', [])} data-testid="clear-initiatives"><Trash2 className="w-3 h-3 mr-1" />Clear All</Button>}
                  <Button variant="outline" size="sm" onClick={() => addEditItem('initiatives', { subtype: 'Ambassador of Smiles', description: '', amount: 0 })} data-testid="add-initiative"><Plus className="w-3 h-3 mr-1" />Add Initiative</Button>
                </div>}
              </CardHeader>
              <CardContent className="space-y-2">
                {(data.initiatives || []).length === 0 ? <p className="text-sm text-muted-foreground py-2 text-center">{editing ? 'Click "+ Add Initiative" above' : 'No initiatives added.'}</p> : (
                  (editing ? editData.initiatives : data.initiatives || []).map((it, i) => (
                    <div key={i} className="p-2 rounded bg-muted/30 relative">
                      {editing && <Button variant="ghost" size="sm" className="absolute top-1 right-1 h-6 w-6 p-0 text-destructive" onClick={() => removeEditItem('initiatives', i)}><Trash2 className="w-3 h-3" /></Button>}
                      {editing ? (
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pr-6">
                          <div className="sm:col-span-1"><Label className="text-xs">Initiative</Label>
                            <Select value={editData.initiatives[i]?.subtype || 'Ambassador of Smiles'} onValueChange={v => updateEditList('initiatives', i, 'subtype', v)}>
                              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Ambassador of Smiles">Ambassador of Smiles</SelectItem>
                                <SelectItem value="Architects of Cultural Exchange">Architects of Cultural Exchange</SelectItem>
                                <SelectItem value="Stewards of Sustainability">Stewards of Sustainability</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Input className="h-7 text-xs" value={editData.initiatives[i]?.description || ''} onChange={e => updateEditList('initiatives', i, 'description', e.target.value)} placeholder="What was done / recipient" /></div>
                          <div><Label className="text-xs">Amount</Label><Input type="number" className="h-7 text-xs" value={editData.initiatives[i]?.amount || 0} onChange={e => updateEditList('initiatives', i, 'amount', e.target.value)} /></div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <div><Badge variant="outline" className="text-xs mr-2">{it.subtype}</Badge><span className="text-muted-foreground">{it.description || '-'}</span></div>
                          <span className="font-medium">{parseFloat(it.amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Care */}
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2"><Heart className="w-4 h-4 text-rose-600" />Care ({(data.care || []).length})</CardTitle>
                {editing && <div className="flex gap-2">
                  {(data.care || []).length > 0 && <Button variant="outline" size="sm" className="text-destructive" onClick={() => updateEdit('care', [])} data-testid="clear-care"><Trash2 className="w-3 h-3 mr-1" />Clear All</Button>}
                  <Button variant="outline" size="sm" onClick={() => addEditItem('care', { beneficiary_type: 'Guest', description: '', amount: 0 })} data-testid="add-care"><Plus className="w-3 h-3 mr-1" />Add Care</Button>
                </div>}
              </CardHeader>
              <CardContent className="space-y-2">
                {(data.care || []).length === 0 ? <p className="text-sm text-muted-foreground py-2 text-center">{editing ? 'Click "+ Add Care" above' : 'No care expenses added.'}</p> : (
                  (editing ? editData.care : data.care || []).map((c, i) => (
                    <div key={i} className="p-2 rounded bg-muted/30 relative">
                      {editing && <Button variant="ghost" size="sm" className="absolute top-1 right-1 h-6 w-6 p-0 text-destructive" onClick={() => removeEditItem('care', i)}><Trash2 className="w-3 h-3" /></Button>}
                      {editing ? (
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pr-6">
                          <div><Label className="text-xs">Beneficiary</Label>
                            <Select value={editData.care[i]?.beneficiary_type || 'Guest'} onValueChange={v => updateEditList('care', i, 'beneficiary_type', v)}>
                              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['Guest', 'Staff', 'Driver', 'Guide', 'Operator', 'Local Caregiver', 'Other'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Input className="h-7 text-xs" value={editData.care[i]?.description || ''} onChange={e => updateEditList('care', i, 'description', e.target.value)} placeholder="Gift / gratitude / complimentary upgrade" /></div>
                          <div><Label className="text-xs">Amount</Label><Input type="number" className="h-7 text-xs" value={editData.care[i]?.amount || 0} onChange={e => updateEditList('care', i, 'amount', e.target.value)} /></div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <div><Badge variant="outline" className="text-xs mr-2">{c.beneficiary_type}</Badge><span className="text-muted-foreground">{c.description || '-'}</span></div>
                          <span className="font-medium">{parseFloat(c.amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Other Expenses */}
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2"><Package className="w-4 h-4 text-slate-600" />Other Services ({(data.other_expenses || []).length})</CardTitle>
                {editing && <div className="flex gap-2">
                  {(data.other_expenses || []).length > 0 && <Button variant="outline" size="sm" className="text-destructive" onClick={() => updateEdit('other_expenses', [])} data-testid="clear-others"><Trash2 className="w-3 h-3 mr-1" />Clear All</Button>}
                  <Button variant="outline" size="sm" onClick={() => addEditItem('other_expenses', { sub_type: 'Misc Trip Expense', description: '', amount: 0 })} data-testid="add-other"><Plus className="w-3 h-3 mr-1" />Add Other</Button>
                </div>}
              </CardHeader>
              <CardContent className="space-y-2">
                {(data.other_expenses || []).length === 0 ? <p className="text-sm text-muted-foreground py-2 text-center">{editing ? 'Click "+ Add Other" above' : 'No other expenses added.'}</p> : (
                  (editing ? editData.other_expenses : data.other_expenses || []).map((o, i) => (
                    <div key={i} className="p-2 rounded bg-muted/30 relative">
                      {editing && <Button variant="ghost" size="sm" className="absolute top-1 right-1 h-6 w-6 p-0 text-destructive" onClick={() => removeEditItem('other_expenses', i)}><Trash2 className="w-3 h-3" /></Button>}
                      {editing ? (
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pr-6">
                          <div><Label className="text-xs">Type</Label>
                            <Select value={editData.other_expenses[i]?.sub_type || 'Misc Trip Expense'} onValueChange={v => updateEditList('other_expenses', i, 'sub_type', v)}>
                              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['ILP', 'Permit', 'Misc Trip Expense', 'Upgrade', 'Other'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Input className="h-7 text-xs" value={editData.other_expenses[i]?.description || ''} onChange={e => updateEditList('other_expenses', i, 'description', e.target.value)} /></div>
                          <div><Label className="text-xs">Amount</Label><Input type="number" className="h-7 text-xs" value={editData.other_expenses[i]?.amount || 0} onChange={e => updateEditList('other_expenses', i, 'amount', e.target.value)} /></div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <div><Badge variant="outline" className="text-xs mr-2">{o.sub_type}</Badge><span className="text-muted-foreground">{o.description || '-'}</span></div>
                          <span className="font-medium">{parseFloat(o.amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Agent Details */}
            {isAgent && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><UserCheck className="w-4 h-4" />Agent Details</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {editing ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div><Label className="text-xs">Agency Name</Label><VendorAutocomplete value={editData.agent_details?.agency_name || ''} vendors={vendors.filter(v => v.type === 'Agent')} placeholder="Type or select agent"
                          onChange={v => setEditData(prev => ({ ...prev, agent_details: { ...(prev.agent_details || {}), agency_name: v } }))}
                          onSelect={v => setEditData(prev => ({ ...prev, agent_details: { ...(prev.agent_details || {}), agency_name: v.name, contact_name: v.contact_name || prev.agent_details?.contact_name || '', contact_number: v.phone || prev.agent_details?.contact_number || '' } }))} /></div>
                        <div><Label className="text-xs">Contact Name</Label><Input className="h-7 text-xs" value={editData.agent_details?.contact_name || ''} onChange={e => setEditData(prev => ({ ...prev, agent_details: { ...(prev.agent_details || {}), contact_name: e.target.value } }))} /></div>
                        <div><Label className="text-xs">Contact Number</Label><Input className="h-7 text-xs" value={editData.agent_details?.contact_number || ''} onChange={e => setEditData(prev => ({ ...prev, agent_details: { ...(prev.agent_details || {}), contact_number: e.target.value } }))} /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                        <div><Label className="text-xs">Total Package</Label><Input type="number" className="h-7 text-xs" value={editData.agent_details?.total_package_cost || 0} onChange={e => setEditData(prev => ({ ...prev, agent_details: { ...(prev.agent_details || {}), total_package_cost: e.target.value } }))} /></div>
                        <div><Label className="text-xs">Advance Paid</Label><Input type="number" className="h-7 text-xs" value={editData.agent_details?.advance_paid || 0} onChange={e => setEditData(prev => ({ ...prev, agent_details: { ...(prev.agent_details || {}), advance_paid: e.target.value } }))} /></div>
                        <div><Label className="text-xs">Balance</Label><Input className="h-7 text-xs bg-muted" value={(parseFloat(editData.agent_details?.total_package_cost || 0) - parseFloat(editData.agent_details?.advance_paid || 0)).toLocaleString('en-IN')} readOnly /></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div className="p-2 rounded bg-muted/30"><p className="text-[10px] text-muted-foreground font-medium uppercase mb-0.5">Agency Name</p><p className="font-medium">{(data.agent_details || {}).agency_name || <span className="text-muted-foreground italic">Not set — click Edit</span>}</p></div>
                        <div className="p-2 rounded bg-muted/30"><p className="text-[10px] text-muted-foreground font-medium uppercase mb-0.5">Contact Person</p><p className="font-medium">{(data.agent_details || {}).contact_name || <span className="text-muted-foreground italic">Not set</span>}</p></div>
                        <div className="p-2 rounded bg-muted/30"><p className="text-[10px] text-muted-foreground font-medium uppercase mb-0.5">Contact Number</p><p className="font-medium">{(data.agent_details || {}).contact_number || <span className="text-muted-foreground italic">Not set</span>}</p></div>
                      </div>
                      <div className="flex gap-4 pt-2 border-t text-sm">
                        <span>Package: <strong>{parseFloat((data.agent_details || {}).total_package_cost || 0).toLocaleString('en-IN')}</strong></span>
                        <span className="text-emerald-600">Paid: {parseFloat((data.agent_details || {}).advance_paid || 0).toLocaleString('en-IN')}</span>
                        <span className="text-red-600">Balance: {(parseFloat((data.agent_details || {}).total_package_cost || 0) - parseFloat((data.agent_details || {}).advance_paid || 0)).toLocaleString('en-IN')}</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Documents Tab — inline preview gallery, scoped to this tour. */}
          <TabsContent value="documents" className="mt-4 space-y-4">
            <DocumentsPanel tourId={id} compact />
            <Card>
              <CardContent className="pt-4">
                <DocumentGallery
                  scope={{ tour_id: id }}
                  category={uploadCategory}
                  title="Tour Documents"
                  emptyHint="No documents uploaded for this tour yet. Upload invoices, itinerary PDFs, or any reference docs — preview inline."
                  testId="tour-docs-gallery"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="mt-4 space-y-4">
            <RazorpayPanel tourId={id} compact
              prefill={{ customer_id: tour?.customer_id || '', customer_name: tour?.customer_name || '', description: `Payment for ${tour?.tour_name || 'tour'}` }} />
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CreditCard className="w-4 h-4" />Payment History</CardTitle></CardHeader>
              <CardContent>
                {payments.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No payments recorded for this tour</p> : (
                  <div className="space-y-2">
                    {payments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 text-sm">
                        <div>
                          <span className="font-medium">{p.vendor_name || 'Payment'}</span>
                          {p.category && <Badge variant="outline" className="text-xs ml-2">{p.category}</Badge>}
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-emerald-600">{p.amount?.toLocaleString('en-IN')}</span>
                          <span className="text-xs text-muted-foreground ml-2">{p.date}</span>
                        </div>
                      </div>
                    ))}
                    <div className="pt-2 border-t text-sm text-right">
                      <span className="text-muted-foreground">Total Paid: </span>
                      <span className="font-bold text-emerald-600">{payments.reduce((s, p) => s + (p.amount || 0), 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity" className="mt-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" />Activity Log</CardTitle></CardHeader>
              <CardContent>
                {activityLogs.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No activity recorded</p> : (
                  <div className="space-y-2">
                    {activityLogs.map((log, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs p-2 rounded-lg bg-muted/30">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                        <div>
                          <span className="font-medium">{log.user_name}</span>
                          <span className="text-muted-foreground"> {log.action} this tour</span>
                          {log.details && <p className="text-muted-foreground mt-0.5">{log.details}</p>}
                          <p className="text-muted-foreground mt-0.5">{log.timestamp?.slice(0, 16).replace('T', ' ')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        <ConfirmDeleteDialog
          open={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={() => deleteConfirm === 'tour' ? handleDelete() : deleteFile(deleteConfirm)}
          title={deleteConfirm === 'tour' ? 'Delete Tour' : 'Delete File'}
          description={deleteConfirm === 'tour' ? 'This will permanently delete this tour and all associated data.' : 'This will remove this file.'}
        />

        {/* Group AI Extract Dialog */}
        <Dialog open={groupExtractOpen} onOpenChange={setGroupExtractOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-600" />AI Extract Group Members</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Upload passport scan / pax list / booking form (image or PDF), or paste text. AI will extract all travellers' names, DOB, notes.</p>
              <div className="flex gap-2 flex-wrap">
                <label className="flex items-center gap-1 cursor-pointer border rounded-md px-3 py-1.5 text-sm hover:bg-muted">
                  <ImageIcon className="w-4 h-4" /> Image
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleGroupExtractFile(e.target.files?.[0])} />
                </label>
                <label className="flex items-center gap-1 cursor-pointer border rounded-md px-3 py-1.5 text-sm hover:bg-muted">
                  <FileText className="w-4 h-4" /> PDF / DOCX / XLSX
                  <input type="file" accept=".pdf,.doc,.docx,.xlsx,.xls,.txt" className="hidden" onChange={e => handleGroupExtractFile(e.target.files?.[0])} />
                </label>
              </div>
              <div>
                <Label>Or paste text</Label>
                <Textarea rows={6} value={groupExtractText} onChange={e => setGroupExtractText(e.target.value)} placeholder="Paste pax list / booking form..." data-testid="group-extract-text" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setGroupExtractOpen(false)}>Close</Button>
                <Button onClick={handleGroupExtractText} disabled={groupExtracting || !groupExtractText.trim()}>
                  {groupExtracting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}Extract from Text
                </Button>
              </div>
              {groupExtracting && <p className="text-xs text-muted-foreground text-center">Extracting... may take 10-20 seconds</p>}
            </div>
          </DialogContent>
        </Dialog>
        {/* Brochures — manager lists every brochure & memories album */}
        {tour && (
          <BrochureManager
            open={brochureOpen}
            onOpenChange={setBrochureOpen}
            ownerKind="tour"
            ownerId={tour.id}
            defaultTitle={tour.tour_id || tour.customer_name || ''}
          />
        )}
      </div>
    </Layout>
  );
};

const DetailField = ({ label, value, editing, onChange, type = 'text' }) => (
  <div>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {editing ? <Input className="h-7 text-sm mt-0.5" type={type} value={value || ''} onChange={e => onChange(e.target.value)} />
      : <p className="text-sm font-medium">{type === 'date' && value ? formatDate(value) : (value || '-')}</p>}
  </div>
);

const VendorAutocomplete = ({ value, onChange, vendors, placeholder, onSelect }) => {
  const [open, setOpen] = React.useState(false);
  const matches = value ? vendors.filter(v => v.name.toLowerCase().includes(value.toLowerCase())) : vendors;
  return (
    <div className="relative">
      <Input className="h-7 text-xs" value={value || ''} placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {open && matches.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-y-auto">
          {matches.slice(0, 6).map(v => (
            <button key={v.id} type="button" className="w-full text-left px-3 py-1.5 hover:bg-muted text-xs"
              onMouseDown={e => { e.preventDefault(); onChange(v.name); if (onSelect) onSelect(v); setOpen(false); }}>
              <span className="font-medium">{v.name}</span>
              {v.phone && <span className="text-muted-foreground ml-2">{v.phone}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PollenDotsTourDetail;
